import React, { useState, useEffect, useRef } from 'react';
import VoiceInputButton from '../../VoiceInputButton';
import { Patient } from '../../../services/firebase/FirebasePatientService';
import { FirebasePatientService } from '../../../services/firebase/FirebasePatientService';
import FormAssignmentService, { FormTemplate, AssignedForm } from '../../../services/FormAssignmentService';
import { FirebaseFormResponseService } from '../../../services/firebase/FirebaseFormResponseService';
import { FirebaseCustomMeasureService, CustomMeasure } from '../../../services/firebase/FirebaseCustomMeasureService';
import { FirebaseFormAssignmentService } from '../../../services/firebase/FirebaseFormAssignmentService';
import { sessionService, SessionData } from '../../../services/SessionService';
import SessionsTabImplementation from './SessionsTabImplementation';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfirm } from '../../../hooks/useConfirm';
import { FirebaseHomeworkService } from '../../../services/firebase/FirebaseHomeworkService';
import { HomeworkAssignment } from '../../../types/client';
import {
  FirebaseDiagnosisPrescriptionService,
  FirebaseDiagnosisRecord,
  FirebasePrescriptionRecord
} from '../../../services/firebase/FirebaseDiagnosisPrescriptionService';
import { useMessaging } from '../../../contexts/MessagingContext';
import {
  FirebaseClinicalNotesService,
  ClinicalNote,
  NoteTemplate,
  NoteStatus
} from '../../../services/firebase/FirebaseClinicalNotesService';
import {
  FirebasePsychotherapyNotesService,
  PsychotherapyNote
} from '../../../services/firebase/FirebasePsychotherapyNotesService';
import TreatmentPlanTab from './TreatmentPlanTab';
import NotesSidebar from './NotesSidebar';
import { firebaseAppointmentService, Appointment } from '../../../services/firebase/FirebaseAppointmentService';
import { appointmentReminderService } from '../../../services/AppointmentReminderService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../config/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import COMMON_DIAGNOSES from '../../../data/icd10Diagnoses';
import PatientEligibilityPanel from '../../eligibility/PatientEligibilityPanel';

const formatTime12 = (t: string): string => {
  if (!t) return t;
  if (t.includes('AM') || t.includes('PM')) return t;
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
};

interface ClientProfileProps {
  client: Patient;
  onBack: () => void;
  onStartSession?: (patient: Patient) => void;
  onScheduleAppointment?: () => void;
  onMessageClient?: (client: Patient) => void;
}

type TabType = 'overview' | 'treatment' | 'measures' | 'files' | 'forms' | 'billing';
type TreatmentSubTab = 'treatment-plan' | 'diagnosis' | 'prescriptions' | 'sessions' | 'notes' | 'homework';

// Forms Tab Component
const FormsTab: React.FC<{ client: Patient }> = ({ client }) => {
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [assignedForms, setAssignedForms] = useState<AssignedForm[]>([]);
  const [formStatus, setFormStatus] = useState<{ total: number; completed: number; pending: number; overdue: number; canBookAppointment: boolean }>({ total: 0, completed: 0, pending: 0, overdue: 0, canBookAppointment: false });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showClearFormsModal, setShowClearFormsModal] = useState(false);
  const [removeAssignmentId, setRemoveAssignmentId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<FormTemplate['category'] | 'all'>('all');
  const [viewingResponses, setViewingResponses] = useState<any>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const formService = FormAssignmentService.getInstance();
  const { user } = useAuth();
  // Use client's assigned provider for queries (so admin staff can see forms too)
  const providerId = (client as any).assignedProviderId || user?.uid || '';

  const handleViewResponses = async (assignment: AssignedForm) => {
    setLoadingResponses(true);
    try {
      // Try provider-scoped responses first, then intake (empty providerId)
      const [providerResponses, intakeResponses] = await Promise.all([
        FirebaseFormResponseService.getResponsesByClient(providerId, client.id),
        FirebaseFormResponseService.getResponsesByClient('', client.id).catch(() => [] as any[]),
      ]);
      const allResponses = [...providerResponses, ...intakeResponses];
      // Also check responses where providerId is the client's own UID (legacy bug)
      const clientSelfResponses = await FirebaseFormResponseService.getResponsesByClient(client.id, client.id).catch(() => [] as any[]);
      allResponses.push(...clientSelfResponses);
      // Deduplicate by ID
      const seen = new Set<string>();
      const unique = allResponses.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
      // Find the matching response for this form template
      const match = unique.find(r => r.formTemplateId === assignment.formTemplateId);
      if (match) {
        setViewingResponses(match);
      } else {
        setViewingResponses({ notFound: true, formTemplateId: assignment.formTemplateId });
      }
    } catch (error) {
      console.error('Error fetching form responses:', error);
      setViewingResponses({ notFound: true, formTemplateId: assignment.formTemplateId });
    }
    setLoadingResponses(false);
  };

  useEffect(() => {
    // Load default templates
    const defaults = formService.getAllFormTemplates();
    setFormTemplates(defaults);

    // Load custom measures and merge as assessment templates
    (async () => {
      try {
        const customMeasures = await FirebaseCustomMeasureService.getPracticeMeasures(providerId);
        if (customMeasures.length > 0) {
          const cmTemplates = FirebaseFormAssignmentService.customMeasuresToTemplates(customMeasures);
          setFormTemplates([...defaults, ...cmTemplates]);
        }
      } catch { /* non-blocking */ }
    })();

    // Subscribe to real-time Firestore assignment updates (no providerId filter — show ALL forms for this client)
    const unsubscribe = formService.subscribeToClientAssignments(client.id, (assignments) => {
      setAssignedForms(assignments);
    });

    // Load async form status (no providerId filter)
    (async () => {
      const status = await formService.getClientFormStatus(client.id);
      setFormStatus(status);
    })();

    return () => {
      unsubscribe();
    };
  }, [client.id, formService]);

  const handleAssignForm = async (formTemplateId: string, priority: AssignedForm['priority'] = 'medium') => {
    // Look up name from full template list (includes custom measures)
    const tmpl = formTemplates.find(t => t.id === formTemplateId);
    if (tmpl) {
      await FirebaseFormAssignmentService.assignFormToClient({
        clientId: client.id, providerId, formTemplateId,
        formTemplateName: tmpl.name, category: tmpl.category,
        required: tmpl.required, priority,
      });
    } else {
      await formService.assignFormToClient(client.id, formTemplateId, providerId, priority);
    }
    setShowAssignModal(false);
    // Refresh form status after assignment change
    const status = await formService.getClientFormStatus(client.id, providerId);
    setFormStatus(status);
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    setRemoveAssignmentId(assignmentId);
  };

  const confirmRemoveAssignment = async () => {
    if (!removeAssignmentId) return;
    await formService.removeFormAssignment(removeAssignmentId);
    setRemoveAssignmentId(null);
    const status = await formService.getClientFormStatus(client.id, providerId);
    setFormStatus(status);
  };

  const filteredTemplates = selectedCategory === 'all'
    ? formTemplates
    : formTemplates.filter(template => template.category === selectedCategory);

  const assignedFormIds = new Set(assignedForms.map(af => af.formTemplateId));
  const availableTemplates = filteredTemplates.filter(template => !assignedFormIds.has(template.id));

  const status = formStatus;

  return (
    <div>
      {/* Forms Status Overview */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#f9fafb' }}>Forms Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{status.completed}</div>
            <div style={{ fontSize: '12px', color: '#d1d5db' }}>Completed</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: status.pending > 0 ? '#f59e0b' : '#6b7280' }}>{status.pending}</div>
            <div style={{ fontSize: '12px', color: '#d1d5db' }}>Pending</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: status.overdue > 0 ? '#ef4444' : '#6b7280' }}>{status.overdue}</div>
            <div style={{ fontSize: '12px', color: '#d1d5db' }}>Overdue</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '12px',
              color: status.canBookAppointment ? '#10b981' : '#ef4444',
              fontWeight: 'bold',
              padding: '4px 8px',
              borderRadius: '4px',
              background: status.canBookAppointment ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'
            }}>
              {status.canBookAppointment ? '✓ Can Book Appointments' : '⚠ Cannot Book Appointments'}
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Forms List */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: 0, color: '#f9fafb' }}>Assigned Forms</h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowClearFormsModal(true)}
              style={{
                background: '#dc2626',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Clear Forms
            </button>
            <button
              onClick={async () => {
                await formService.assignStandardFormsToNewClient(client.id, providerId);
                const updatedStatus = await formService.getClientFormStatus(client.id, providerId);
                setFormStatus(updatedStatus);
              }}
              style={{
                background: '#f59e0b',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Quick Assign Required
            </button>
            <button
              onClick={() => setShowAssignModal(true)}
              style={{
                background: '#58A6FF',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Assign Forms
            </button>
          </div>
        </div>

        {assignedForms.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#d1d5db', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No forms assigned</div>
            <div style={{ fontSize: '14px' }}>Click "Assign Forms" to get started</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {assignedForms.map((assignment) => {
              const template = formService.getFormTemplateById(assignment.formTemplateId)
                || { id: assignment.formTemplateId, name: assignment.formTemplateName || assignment.formTemplateId, category: assignment.category || 'assessment', required: false };

              return (
                <div key={assignment.id} style={{
                  background: '#16325B',
                  border: `2px solid ${assignment.completed ? '#10b981' : assignment.priority === 'high' ? '#ef4444' : '#6b7280'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: assignment.completed ? '#10b981' : '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {assignment.completed ? '✓' : '○'}
                      </div>
                      <div>
                        <div style={{ color: '#f9fafb', fontWeight: '600', fontSize: '16px' }}>{template.name}</div>
                        <div style={{ color: '#d1d5db', fontSize: '12px' }}>
                          {template.category.replace('-', ' ').toUpperCase()}
                          {template.required && <span style={{ color: '#ef4444', marginLeft: '8px' }}>• Required</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ color: '#d1d5db', fontSize: '12px' }}>
                      Assigned: {new Date(assignment.assignedDate).toLocaleDateString()}
                      {assignment.completed && assignment.completedDate && (
                        <span> • Completed: {new Date(assignment.completedDate).toLocaleDateString()}</span>
                      )}
                      {assignment.dueDate && !assignment.completed && (
                        <span style={{ color: new Date(assignment.dueDate) < new Date() ? '#ef4444' : '#f59e0b' }}>
                          • Due: {new Date(assignment.dueDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {assignment.completed && (
                      <button
                        onClick={() => handleViewResponses(assignment)}
                        disabled={loadingResponses}
                        style={{
                          background: '#58A6FF',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                      >
                        View
                      </button>
                    )}
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      background: assignment.priority === 'high' ? '#ef4444' : assignment.priority === 'medium' ? '#f59e0b' : '#6b7280',
                      color: 'white'
                    }}>
                      {assignment.priority.toUpperCase()}
                    </span>
                    <button
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '4px'
                      }}
                      title="Remove assignment"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assign Forms Modal */}
      {showAssignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#16325B',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#f9fafb' }}>Assign Forms to {`${client.firstName} ${client.lastName}`}</h3>
              <button
                onClick={() => setShowAssignModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>

            {/* Category Filter */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['all', 'intake', 'consent', 'assessment', 'treatment-plan'].map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category as any)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: 'none',
                      background: selectedCategory === category ? '#58A6FF' : '#28527A',
                      color: selectedCategory === category ? 'white' : '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textTransform: 'capitalize'
                    }}
                  >
                    {category === 'treatment-plan' ? 'Treatment Plans' : category === 'all' ? 'All Forms' : category}
                  </button>
                ))}
              </div>
            </div>

            {/* Available Forms */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {availableTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#d1d5db', padding: '20px' }}>
                  {selectedCategory === 'all' ? 'All available forms have been assigned' : `No ${selectedCategory} forms available to assign`}
                </div>
              ) : (
                availableTemplates.map((template) => (
                  <div key={template.id} style={{
                    background: '#28527A',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f9fafb', fontWeight: '600', marginBottom: '4px' }}>{template.name}</div>
                      <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>
                        {template.category.replace('-', ' ').toUpperCase()}
                        {template.required && <span style={{ color: '#ef4444', marginLeft: '8px' }}>• Required</span>}
                      </div>
                      {template.description && (
                        <div style={{ color: '#6CA6C1', fontSize: '12px' }}>{template.description}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAssignForm(template.id, 'low')}
                        style={{
                          background: '#6b7280',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Low Priority
                      </button>
                      <button
                        onClick={() => handleAssignForm(template.id, 'medium')}
                        style={{
                          background: '#f59e0b',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Medium
                      </button>
                      <button
                        onClick={() => handleAssignForm(template.id, 'high')}
                        style={{
                          background: '#ef4444',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        High Priority
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setShowAssignModal(false)}
                style={{
                  background: '#6b7280',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Forms Confirmation Modal */}
      {showClearFormsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#16325B',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#f9fafb' }}>Clear All Form Assignments</h3>
              <button
                onClick={() => setShowClearFormsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#d1d5db', fontSize: '16px', lineHeight: '1.5', marginBottom: '24px' }}>
              Are you sure you want to clear all form assignments for <strong>{`${client.firstName} ${client.lastName}`}</strong>?
              <br /><br />
              This action cannot be undone and will remove all assigned forms.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowClearFormsModal(false)}
                style={{
                  background: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await formService.clearAllClientAssignments(client.id, providerId);
                  setShowClearFormsModal(false);
                  const updatedStatus = await formService.getClientFormStatus(client.id, providerId);
                  setFormStatus(updatedStatus);
                }}
                style={{
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear All Forms
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Responses Modal */}
      {viewingResponses && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#16325B', borderRadius: '8px', padding: '24px',
            maxWidth: '700px', width: '90%', maxHeight: '80vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f9fafb' }}>
                {viewingResponses.notFound ? 'No Responses Found' : (viewingResponses.formTemplateName || viewingResponses.formTemplateId)}
              </h3>
              <button onClick={() => setViewingResponses(null)}
                style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '20px' }}>×</button>
            </div>
            {viewingResponses.notFound ? (
              <p style={{ color: '#d1d5db', textAlign: 'center', padding: '20px' }}>
                No response data found for this form. The client may have completed it before the provider link was established.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {viewingResponses.submittedAt && (
                  <div style={{ color: '#d1d5db', fontSize: '13px', marginBottom: '8px' }}>
                    Submitted: {new Date(viewingResponses.submittedAt?.toDate?.() || viewingResponses.submittedAt).toLocaleDateString()}
                  </div>
                )}
                {viewingResponses.responses?.map((r: any, i: number) => {
                  const answerVal = typeof r.answer === 'number' ? r.answer : r.answer;
                  const isScored = typeof r.answer === 'number' || (typeof r.answer === 'string' && /^[0-3]$/.test(r.answer));
                  return (
                    <div key={i} style={{
                      background: '#28527A', borderRadius: '6px', padding: '12px 16px',
                      borderLeft: `3px solid ${isScored ? '#D4A017' : '#58A6FF'}`
                    }}>
                      <div style={{ color: '#e5e7eb', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                        {r.questionText || r.questionId}
                      </div>
                      <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                        {isScored ? (
                          <span style={{
                            background: Number(answerVal) >= 2 ? '#ef4444' : Number(answerVal) >= 1 ? '#f59e0b' : '#10b981',
                            padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, color: 'white'
                          }}>
                            {answerVal} — {['Not at all', 'Several days', 'More than half the days', 'Nearly every day'][Number(answerVal)] || answerVal}
                          </span>
                        ) : (
                          <span>{String(answerVal)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Show total score for scored measures */}
                {viewingResponses.responses && (() => {
                  const scored = viewingResponses.responses.filter((r: any) =>
                    r.questionId !== 'difficultyLevel' && (typeof r.answer === 'number' || /^[0-3]$/.test(String(r.answer)))
                  );
                  if (scored.length > 0) {
                    const total = scored.reduce((sum: number, r: any) => sum + Number(r.answer), 0);
                    return (
                      <div style={{
                        background: '#D4A017', borderRadius: '6px', padding: '12px 16px',
                        textAlign: 'center', marginTop: '8px'
                      }}>
                        <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
                          Total Score: {total}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setViewingResponses(null)}
                style={{ background: '#6b7280', border: 'none', borderRadius: '4px', padding: '8px 16px', color: 'white', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Form Assignment Confirmation Modal */}
      {removeAssignmentId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#16325B',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#f9fafb' }}>Remove Form Assignment</h3>
              <button
                onClick={() => setRemoveAssignmentId(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#d1d5db', fontSize: '16px', lineHeight: '1.5', marginBottom: '24px' }}>
              Are you sure you want to remove this form assignment? This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRemoveAssignmentId(null)}
                style={{
                  background: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveAssignment}
                style={{
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Treatment Sub-tab Components
// Common psychiatric medications for dropdown
const COMMON_MEDICATIONS = [
  { name: 'Sertraline (Zoloft)', defaultDosage: '50mg', category: 'SSRI' },
  { name: 'Escitalopram (Lexapro)', defaultDosage: '10mg', category: 'SSRI' },
  { name: 'Fluoxetine (Prozac)', defaultDosage: '20mg', category: 'SSRI' },
  { name: 'Paroxetine (Paxil)', defaultDosage: '20mg', category: 'SSRI' },
  { name: 'Venlafaxine (Effexor)', defaultDosage: '75mg', category: 'SNRI' },
  { name: 'Duloxetine (Cymbalta)', defaultDosage: '60mg', category: 'SNRI' },
  { name: 'Bupropion (Wellbutrin)', defaultDosage: '150mg', category: 'NDRI' },
  { name: 'Mirtazapine (Remeron)', defaultDosage: '15mg', category: 'Atypical' },
  { name: 'Alprazolam (Xanax)', defaultDosage: '0.5mg', category: 'Benzodiazepine' },
  { name: 'Lorazepam (Ativan)', defaultDosage: '1mg', category: 'Benzodiazepine' },
  { name: 'Clonazepam (Klonopin)', defaultDosage: '0.5mg', category: 'Benzodiazepine' },
  { name: 'Aripiprazole (Abilify)', defaultDosage: '5mg', category: 'Antipsychotic' },
  { name: 'Quetiapine (Seroquel)', defaultDosage: '50mg', category: 'Antipsychotic' },
  { name: 'Risperidone (Risperdal)', defaultDosage: '2mg', category: 'Antipsychotic' },
  { name: 'Methylphenidate (Ritalin)', defaultDosage: '10mg', category: 'Stimulant' },
  { name: 'Amphetamine/Dextroamphetamine (Adderall)', defaultDosage: '10mg', category: 'Stimulant' },
  { name: 'Lamotrigine (Lamictal)', defaultDosage: '100mg', category: 'Mood Stabilizer' },
  { name: 'Lithium Carbonate', defaultDosage: '300mg', category: 'Mood Stabilizer' }
];

const FREQUENCY_OPTIONS = [
  'Once daily',
  'Twice daily',
  'Three times daily',
  'Four times daily',
  'Every morning',
  'Every evening',
  'Before bedtime',
  'As needed',
  'Every 4 hours as needed',
  'Every 6 hours as needed'
];

const DiagnosisTab: React.FC<{ client: Patient }> = ({ client }) => {
  const { confirm, ConfirmDialog: DiagConfirmDialog } = useConfirm();
  const toast = useToast();
  const { user, roles } = useAuth();
  const providerId = user?.uid || '';
  const providerName = user?.displayName || 'Provider';
  const isAdminOrDirector = roles.includes('system_admin') || roles.includes('clinical_director');
  const [diagnoses, setDiagnoses] = useState<FirebaseDiagnosisRecord[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState('');
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [customName, setCustomName] = useState('');
  const [useCustomDiagnosis, setUseCustomDiagnosis] = useState(false);
  const [diagnosisDate, setDiagnosisDate] = useState(new Date().toISOString().split('T')[0]);
  const [diagnosisStatus, setDiagnosisStatus] = useState<'active' | 'resolved' | 'chronic'>('active');
  const [diagnosisNotes, setDiagnosisNotes] = useState('');
  const diagSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!client.id) return;
    const unsubscribe = isAdminOrDirector
      ? FirebaseDiagnosisPrescriptionService.subscribeToClientDiagnoses(client.id, (data) => setDiagnoses(data))
      : FirebaseDiagnosisPrescriptionService.subscribeToDiagnoses(client.id, providerId, (data) => setDiagnoses(data));
    return () => unsubscribe();
  }, [client.id, providerId, isAdminOrDirector]);

  const handleAddDiagnosis = async () => {
    let code: string;
    let name: string;

    if (useCustomDiagnosis) {
      if (!customCode.trim() || !customName.trim()) return;
      code = customCode.trim();
      name = customName.trim();
    } else {
      if (!selectedDiagnosis) return;
      const diagnosis = COMMON_DIAGNOSES.find(d => `${d.code}|${d.name}` === selectedDiagnosis);
      if (!diagnosis) return;
      code = diagnosis.code;
      name = diagnosis.name;
    }

    setSaving(true);
    try {
      await FirebaseDiagnosisPrescriptionService.addDiagnosis({
        code,
        name,
        diagnosedDate: diagnosisDate,
        diagnosedBy: providerName,
        providerId,
        patientId: client.id,
        status: diagnosisStatus,
        notes: diagnosisNotes || undefined
      });

      // Reset form
      setSelectedDiagnosis('');
      setDiagnosisSearch('');
      setCustomCode('');
      setCustomName('');
      setUseCustomDiagnosis(false);
      setDiagnosisDate(new Date().toISOString().split('T')[0]);
      setDiagnosisStatus('active');
      setDiagnosisNotes('');
      setShowAddForm(false);
      toast.success('Diagnosis Added', `${name} added successfully.`);
    } catch (error: any) {
      console.error('Failed to add diagnosis:', error);
      toast.error('Failed to Add Diagnosis', error?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDiagnosis = async (diagnosisId: string) => {
    const confirmed = await confirm({
      title: 'Delete Diagnosis',
      message: 'Are you sure you want to delete this diagnosis?',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (confirmed) {
      try {
        await FirebaseDiagnosisPrescriptionService.deleteDiagnosis(diagnosisId, providerId, client.id);
        toast.success('Deleted', 'Diagnosis removed.');
      } catch (error: any) {
        console.error('Failed to delete diagnosis:', error);
        toast.error('Delete Failed', error?.message || 'Please try again.');
      }
    }
  };

  const handleUpdateStatus = async (diagnosisId: string, newStatus: 'active' | 'resolved' | 'chronic') => {
    await FirebaseDiagnosisPrescriptionService.updateDiagnosis(diagnosisId, { status: newStatus }, providerId, client.id);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#f59e0b';
      case 'resolved': return '#10b981';
      case 'chronic': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <>
    {DiagConfirmDialog}
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #6CA6C1'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#F7E9A0',
          margin: 0
        }}>
          Diagnosis
        </h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          style={{
            background: '#58A6FF',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {showAddForm ? 'Cancel' : '+ Add Diagnosis'}
        </button>
      </div>

      {/* Add Diagnosis Form */}
      {showAddForm && (
        <div style={{
          backgroundColor: '#16325B',
          padding: '20px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: '#F7E9A0', marginBottom: '16px' }}>Add New Diagnosis</h4>

          {/* Mode toggle: search list vs custom */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <button
              onClick={() => { setUseCustomDiagnosis(false); setCustomCode(''); setCustomName(''); }}
              style={{
                padding: '6px 14px', borderRadius: '4px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                background: !useCustomDiagnosis ? '#58A6FF' : 'transparent',
                border: !useCustomDiagnosis ? 'none' : '1px solid #6CA6C1',
                color: '#f9fafb'
              }}
            >
              Common Diagnoses
            </button>
            <button
              onClick={() => { setUseCustomDiagnosis(true); setSelectedDiagnosis(''); setDiagnosisSearch(''); }}
              style={{
                padding: '6px 14px', borderRadius: '4px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                background: useCustomDiagnosis ? '#58A6FF' : 'transparent',
                border: useCustomDiagnosis ? 'none' : '1px solid #6CA6C1',
                color: '#f9fafb'
              }}
            >
              Custom Code
            </button>
          </div>

          {!useCustomDiagnosis ? (
            <div style={{ marginBottom: '16px', position: 'relative' }} ref={diagSearchRef}>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Search Diagnosis *
              </label>
              <input
                type="text"
                value={selectedDiagnosis ? `${COMMON_DIAGNOSES.find(d => `${d.code}|${d.name}` === selectedDiagnosis)?.code} - ${COMMON_DIAGNOSES.find(d => `${d.code}|${d.name}` === selectedDiagnosis)?.name}` : diagnosisSearch}
                onChange={(e) => {
                  setDiagnosisSearch(e.target.value);
                  setSelectedDiagnosis('');
                  setShowDiagnosisDropdown(true);
                }}
                onFocus={() => setShowDiagnosisDropdown(true)}
                placeholder="Type to search (e.g., anxiety, F41, PTSD)..."
                style={{
                  width: '100%', padding: '10px', borderRadius: '4px',
                  border: '1px solid #6CA6C1', background: '#28527A',
                  color: '#e5e7eb', fontSize: '14px'
                }}
              />
              {selectedDiagnosis && (
                <button
                  onClick={() => { setSelectedDiagnosis(''); setDiagnosisSearch(''); }}
                  style={{
                    position: 'absolute', right: '8px', top: '36px',
                    background: 'none', border: 'none', color: '#d1d5db',
                    cursor: 'pointer', fontSize: '16px', padding: '4px'
                  }}
                >
                  ✕
                </button>
              )}
              {showDiagnosisDropdown && !selectedDiagnosis && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  backgroundColor: '#16325B', border: '1px solid #6CA6C1',
                  borderRadius: '0 0 4px 4px', maxHeight: '240px', overflowY: 'auto'
                }}>
                  {COMMON_DIAGNOSES
                    .filter(d => {
                      if (!diagnosisSearch) return true;
                      const term = diagnosisSearch.toLowerCase();
                      return d.code.toLowerCase().includes(term) || d.name.toLowerCase().includes(term);
                    })
                    .slice(0, 20)
                    .map(d => (
                      <button
                        key={d.code}
                        onClick={() => {
                          setSelectedDiagnosis(`${d.code}|${d.name}`);
                          setDiagnosisSearch('');
                          setShowDiagnosisDropdown(false);
                        }}
                        style={{
                          width: '100%', padding: '8px 12px', background: 'none',
                          border: 'none', borderBottom: '1px solid #2A5470',
                          color: '#e5e7eb', fontSize: '14px', textAlign: 'left',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <span style={{ color: '#58A6FF', fontWeight: '500' }}>{d.code}</span>
                        <span style={{ color: '#d1d5db' }}> — </span>
                        {d.name}
                      </button>
                    ))
                  }
                  {COMMON_DIAGNOSES.filter(d => {
                    if (!diagnosisSearch) return true;
                    const term = diagnosisSearch.toLowerCase();
                    return d.code.toLowerCase().includes(term) || d.name.toLowerCase().includes(term);
                  }).length === 0 && (
                    <div style={{ padding: '12px', color: '#d1d5db', fontSize: '13px', textAlign: 'center' }}>
                      No matches. <button onClick={() => { setUseCustomDiagnosis(true); setShowDiagnosisDropdown(false); }}
                        style={{ background: 'none', border: 'none', color: '#58A6FF', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' }}>
                        Enter manually
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                  ICD-10 Code *
                </label>
                <input
                  type="text"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                  placeholder="e.g., F41.9"
                  style={{
                    width: '100%', padding: '10px', borderRadius: '4px',
                    border: '1px solid #6CA6C1', background: '#28527A',
                    color: '#e5e7eb', fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                  Diagnosis Name *
                </label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g., Anxiety disorder, unspecified"
                  style={{
                    width: '100%', padding: '10px', borderRadius: '4px',
                    border: '1px solid #6CA6C1', background: '#28527A',
                    color: '#e5e7eb', fontSize: '14px'
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
              Date Diagnosed *
            </label>
            <input
              type="date"
              value={diagnosisDate}
              onChange={(e) => setDiagnosisDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #6CA6C1',
                background: '#28527A',
                color: '#e5e7eb',
                fontSize: '14px',
                colorScheme: 'dark'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
              Status
            </label>
            <select
              value={diagnosisStatus}
              onChange={(e) => setDiagnosisStatus(e.target.value as 'active' | 'resolved' | 'chronic')}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #6CA6C1',
                background: '#28527A',
                color: '#e5e7eb',
                fontSize: '14px'
              }}
            >
              <option value="active">Active</option>
              <option value="chronic">Chronic</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
              Notes (Optional)
            </label>
            <textarea
              value={diagnosisNotes}
              onChange={(e) => setDiagnosisNotes(e.target.value)}
              placeholder="Add any additional notes about this diagnosis..."
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #6CA6C1',
                background: '#28527A',
                color: '#e5e7eb',
                fontSize: '14px',
                minHeight: '80px',
                resize: 'vertical'
              }}
            />
          </div>

          {(() => {
            const isValid = useCustomDiagnosis
              ? (customCode.trim() && customName.trim())
              : !!selectedDiagnosis;
            return (
              <button
                onClick={handleAddDiagnosis}
                disabled={!isValid || saving}
                style={{
                  background: (isValid && !saving) ? '#10b981' : '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  color: 'white',
                  cursor: (isValid && !saving) ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {saving ? 'Saving...' : 'Add Diagnosis'}
              </button>
            );
          })()}
        </div>
      )}

      {/* Diagnosis List */}
      {diagnoses.length === 0 ? (
        <div style={{
          backgroundColor: '#16325B',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1'
        }}>
          <p style={{ color: '#d1d5db', fontStyle: 'italic', margin: 0 }}>
            No diagnoses recorded yet. Click "Add Diagnosis" to get started.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {diagnoses.map(diagnosis => (
            <div
              key={diagnosis.id}
              style={{
                backgroundColor: '#16325B',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #6CA6C1'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <h4 style={{ color: '#F7E9A0', margin: 0, fontSize: '16px' }}>
                      {diagnosis.name}
                    </h4>
                    <span style={{
                      background: getStatusColor(diagnosis.status),
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {diagnosis.status}
                    </span>
                  </div>
                  <p style={{ color: '#d1d5db', margin: '4px 0', fontSize: '14px' }}>
                    ICD-10: {diagnosis.code}
                  </p>
                  <p style={{ color: '#d1d5db', margin: '4px 0', fontSize: '14px' }}>
                    Diagnosed: {new Date(diagnosis.diagnosedDate).toLocaleDateString()}
                  </p>
                  {diagnosis.notes && (
                    <p style={{ color: '#e5e7eb', margin: '8px 0 0 0', fontSize: '14px', fontStyle: 'italic' }}>
                      "{diagnosis.notes}"
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={diagnosis.status}
                    onChange={(e) => handleUpdateStatus(diagnosis.id, e.target.value as 'active' | 'resolved' | 'chronic')}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: '1px solid #6CA6C1',
                      background: '#28527A',
                      color: '#e5e7eb',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="chronic">Chronic</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <button
                    onClick={() => handleDeleteDiagnosis(diagnosis.id)}
                    style={{
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
};

const PrescriptionsTab: React.FC<{ client: Patient }> = ({ client }) => {
  const { confirm, ConfirmDialog: PrescConfirmDialog } = useConfirm();
  const toast = useToast();
  const { user, roles } = useAuth();
  const providerId = user?.uid || '';
  const providerName = user?.displayName || 'Provider';
  const isAdminOrDirector = roles.includes('system_admin') || roles.includes('clinical_director');
  const [prescriptions, setPrescriptions] = useState<FirebasePrescriptionRecord[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState('');
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('Once daily');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [prescriptionStatus, setPrescriptionStatus] = useState<'active' | 'discontinued' | 'completed'>('active');
  const [refills, setRefills] = useState('');
  const [pharmacy, setPharmacy] = useState('');
  const [prescriptionNotes, setPrescriptionNotes] = useState('');

  useEffect(() => {
    if (!client.id) return;
    const unsubscribe = isAdminOrDirector
      ? FirebaseDiagnosisPrescriptionService.subscribeToClientPrescriptions(client.id, (data) => setPrescriptions(data))
      : FirebaseDiagnosisPrescriptionService.subscribeToPrescriptions(client.id, providerId, (data) => setPrescriptions(data));
    return () => unsubscribe();
  }, [client.id, providerId, isAdminOrDirector]);

  const handleMedicationSelect = (value: string) => {
    setSelectedMedication(value);
    const med = COMMON_MEDICATIONS.find(m => m.name === value);
    if (med) {
      setDosage(med.defaultDosage);
    }
  };

  const handleAddPrescription = async () => {
    if (!selectedMedication || !dosage) return;

    setSaving(true);
    try {
      await FirebaseDiagnosisPrescriptionService.addPrescription({
        medicationName: selectedMedication,
        dosage,
        frequency,
        prescribedDate: new Date().toISOString(),
        prescribedBy: providerName,
        providerId,
        patientId: client.id,
        status: prescriptionStatus,
        startDate,
        endDate: endDate || undefined,
        refillsRemaining: refills ? parseInt(refills) : undefined,
        pharmacy: pharmacy || undefined,
        notes: prescriptionNotes || undefined
      });

      setSelectedMedication('');
      setDosage('');
      setFrequency('Once daily');
      setStartDate(new Date().toISOString().split('T')[0]);
      setEndDate('');
      setPrescriptionStatus('active');
      setRefills('');
      setPharmacy('');
      setPrescriptionNotes('');
      setShowAddForm(false);
      toast.success('Prescription Added', `${selectedMedication} added successfully.`);
    } catch (error: any) {
      console.error('Failed to add prescription:', error);
      toast.error('Failed to Add Prescription', error?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrescription = async (prescriptionId: string) => {
    const confirmed = await confirm({ title: 'Delete Prescription', message: 'Are you sure you want to delete this prescription?', confirmLabel: 'Delete', variant: 'danger' });
    if (confirmed) {
      try {
        await FirebaseDiagnosisPrescriptionService.deletePrescription(prescriptionId, providerId, client.id);
        toast.success('Deleted', 'Prescription removed.');
      } catch (error: any) {
        console.error('Failed to delete prescription:', error);
        toast.error('Delete Failed', error?.message || 'Please try again.');
      }
    }
  };

  const handleUpdateStatus = async (prescriptionId: string, newStatus: 'active' | 'discontinued' | 'completed') => {
    try {
      await FirebaseDiagnosisPrescriptionService.updatePrescription(prescriptionId, { status: newStatus }, providerId, client.id);
    } catch (error: any) {
      console.error('Failed to update prescription status:', error);
      toast.error('Update Failed', error?.message || 'Please try again.');
    }
  };

  const getPrescriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'discontinued': return '#ef4444';
      case 'completed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #6CA6C1'
    }}>
      {PrescConfirmDialog}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#F7E9A0',
          margin: 0
        }}>
          Prescriptions
        </h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          style={{
            background: '#58A6FF',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {showAddForm ? 'Cancel' : '+ Add Prescription'}
        </button>
      </div>

      {showAddForm && (
        <div style={{
          backgroundColor: '#16325B',
          padding: '20px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: '#F7E9A0', marginBottom: '16px' }}>Add New Prescription</h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Select Medication *
              </label>
              <select
                value={selectedMedication}
                onChange={(e) => handleMedicationSelect(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="">-- Select a medication --</option>
                {COMMON_MEDICATIONS.map(med => (
                  <option key={med.name} value={med.name}>
                    {med.name} - {med.category}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Dosage *
              </label>
              <input
                type="text"
                value={dosage}
                onChange={(e) => setDosage(e.target.value)}
                placeholder="e.g., 50mg"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Frequency
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              >
                {FREQUENCY_OPTIONS.map(freq => (
                  <option key={freq} value={freq}>{freq}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                End Date (Optional)
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Refills Remaining
              </label>
              <input
                type="number"
                value={refills}
                onChange={(e) => setRefills(e.target.value)}
                placeholder="0"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Status
              </label>
              <select
                value={prescriptionStatus}
                onChange={(e) => setPrescriptionStatus(e.target.value as 'active' | 'discontinued' | 'completed')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="active">Active</option>
                <option value="discontinued">Discontinued</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Pharmacy (Optional)
              </label>
              <input
                type="text"
                value={pharmacy}
                onChange={(e) => setPharmacy(e.target.value)}
                placeholder="e.g., CVS Pharmacy, 123 Main St"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px' }}>
                Notes (Optional)
              </label>
              <textarea
                value={prescriptionNotes}
                onChange={(e) => setPrescriptionNotes(e.target.value)}
                placeholder="Add any additional notes..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #6CA6C1',
                  background: '#28527A',
                  color: '#e5e7eb',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          <button
            onClick={handleAddPrescription}
            disabled={!selectedMedication || !dosage || saving}
            style={{
              background: (selectedMedication && dosage && !saving) ? '#10b981' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              color: 'white',
              cursor: (selectedMedication && dosage && !saving) ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              marginTop: '16px'
            }}
          >
            {saving ? 'Saving...' : 'Add Prescription'}
          </button>
        </div>
      )}

      {prescriptions.length === 0 ? (
        <div style={{
          backgroundColor: '#16325B',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1'
        }}>
          <p style={{ color: '#d1d5db', fontStyle: 'italic', margin: 0 }}>
            No prescriptions recorded yet. Click "Add Prescription" to get started.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {prescriptions.map(prescription => (
            <div
              key={prescription.id}
              style={{
                backgroundColor: '#16325B',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #6CA6C1'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{ color: '#F7E9A0', margin: 0, fontSize: '16px' }}>
                      {prescription.medicationName}
                    </h4>
                    <span style={{
                      background: getPrescriptionStatusColor(prescription.status),
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {prescription.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', color: '#d1d5db', fontSize: '14px' }}>
                    <p style={{ margin: '4px 0' }}>
                      <strong>Dosage:</strong> {prescription.dosage}
                    </p>
                    <p style={{ margin: '4px 0' }}>
                      <strong>Frequency:</strong> {prescription.frequency}
                    </p>
                    <p style={{ margin: '4px 0' }}>
                      <strong>Start Date:</strong> {new Date(prescription.startDate).toLocaleDateString()}
                    </p>
                    {prescription.endDate && (
                      <p style={{ margin: '4px 0' }}>
                        <strong>End Date:</strong> {new Date(prescription.endDate).toLocaleDateString()}
                      </p>
                    )}
                    {prescription.refillsRemaining !== undefined && (
                      <p style={{ margin: '4px 0' }}>
                        <strong>Refills:</strong> {prescription.refillsRemaining}
                      </p>
                    )}
                    {prescription.pharmacy && (
                      <p style={{ margin: '4px 0' }}>
                        <strong>Pharmacy:</strong> {prescription.pharmacy}
                      </p>
                    )}
                  </div>
                  {prescription.notes && (
                    <p style={{ color: '#e5e7eb', margin: '8px 0 0 0', fontSize: '14px', fontStyle: 'italic' }}>
                      "{prescription.notes}"
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                  <select
                    value={prescription.status}
                    onChange={(e) => handleUpdateStatus(prescription.id, e.target.value as 'active' | 'discontinued' | 'completed')}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: '1px solid #6CA6C1',
                      background: '#28527A',
                      color: '#e5e7eb',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="discontinued">Discontinued</option>
                    <option value="completed">Completed</option>
                  </select>
                  <button
                    onClick={() => handleDeletePrescription(prescription.id)}
                    style={{
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface SessionRecordingDoc {
  id: string;
  sessionId: string;
  appointmentId: string;
  providerId: string;
  patientId: string;
  duration: number;
  hasTranscription: boolean;
  hasNoteSuggestion: boolean;
  hasSummary: boolean;
  transcriptionText: string | null;
  utteranceCount: number;
  speakerCount: number;
  noteSuggestion: {
    template: string;
    content: string;
    sections: Record<string, string>;
    confidence: number;
    warnings: string[];
  } | null;
  summary: {
    providerSummary: string;
    clientSummary: string;
    keyTopics: string[];
    interventionsUsed: string[];
    homeworkAssigned: string[];
    nextSessionFocus: string[];
    riskFlags: string[];
  } | null;
  status: string;
  createdAt: Timestamp;
}

const SessionsTab: React.FC<{ client: Patient }> = ({ client }) => {
  const [recordings, setRecordings] = useState<SessionRecordingDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'transcript' | 'note' | 'summary'>('transcript');

  useEffect(() => {
    const loadRecordings = async () => {
      try {
        const q = query(
          collection(db, 'session_recordings'),
          where('patientId', '==', client.id),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as SessionRecordingDoc));
        setRecordings(docs);
      } catch (err) {
        console.error('Failed to load session recordings:', err);
      }
      setLoading(false);
    };
    loadRecordings();
  }, [client.id]);

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0 min';
    const mins = Math.round(seconds / 60);
    return mins < 1 ? '<1 min' : `${mins} min`;
  };

  const formatDate = (ts: Timestamp): string => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #6CA6C1'
    }}>
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#F7E9A0',
        marginBottom: '16px'
      }}>
        Sessions & Transcripts
      </h3>
      <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
        Session history, notes, and transcripts for {`${client.firstName} ${client.lastName}`}.
      </p>

      {loading ? (
        <p style={{ color: '#9ca3af' }}>Loading sessions...</p>
      ) : recordings.length === 0 ? (
        <div style={{
          backgroundColor: '#16325B',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1'
        }}>
          <p style={{ color: '#d1d5db', fontStyle: 'italic' }}>
            No sessions recorded yet. Sessions and transcripts will appear here after appointments.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {recordings.map(rec => {
            const isExpanded = expandedId === rec.id;
            return (
              <div key={rec.id} style={{
                backgroundColor: '#16325B',
                borderRadius: '8px',
                border: isExpanded ? '1px solid #D4A017' : '1px solid #6CA6C1',
                overflow: 'hidden',
              }}>
                {/* Session header — click to expand */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : rec.id)}
                  style={{
                    padding: '14px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ color: '#D4A017', fontSize: '14px' }}>{isExpanded ? '▼' : '▶'}</span>
                    <div>
                      <div style={{ color: '#f9fafb', fontWeight: '600', fontSize: '14px' }}>
                        {formatDate(rec.createdAt)}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px' }}>
                        {formatDuration(rec.duration)} • {rec.speakerCount} speaker{rec.speakerCount !== 1 ? 's' : ''} • {rec.utteranceCount} utterances
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {rec.hasTranscription && (
                      <span style={{ backgroundColor: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Transcript</span>
                    )}
                    {rec.hasNoteSuggestion && (
                      <span style={{ backgroundColor: '#2563EB', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>AI Note</span>
                    )}
                    {rec.hasSummary && (
                      <span style={{ backgroundColor: '#7C3AED', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Summary</span>
                    )}
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #6CA6C1' }}>
                    {/* Section tabs */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #374151', backgroundColor: '#1e3a5f' }}>
                      {rec.hasTranscription && (
                        <button
                          onClick={() => setActiveSection('transcript')}
                          style={{
                            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeSection === 'transcript' ? '#28527A' : 'transparent',
                            color: activeSection === 'transcript' ? '#D4A017' : '#9ca3af',
                            borderBottom: activeSection === 'transcript' ? '2px solid #D4A017' : '2px solid transparent',
                          }}
                        >
                          Transcript
                        </button>
                      )}
                      {rec.hasNoteSuggestion && (
                        <button
                          onClick={() => setActiveSection('note')}
                          style={{
                            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeSection === 'note' ? '#28527A' : 'transparent',
                            color: activeSection === 'note' ? '#D4A017' : '#9ca3af',
                            borderBottom: activeSection === 'note' ? '2px solid #D4A017' : '2px solid transparent',
                          }}
                        >
                          AI Clinical Note
                        </button>
                      )}
                      {rec.hasSummary && (
                        <button
                          onClick={() => setActiveSection('summary')}
                          style={{
                            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeSection === 'summary' ? '#28527A' : 'transparent',
                            color: activeSection === 'summary' ? '#D4A017' : '#9ca3af',
                            borderBottom: activeSection === 'summary' ? '2px solid #D4A017' : '2px solid transparent',
                          }}
                        >
                          Session Summary
                        </button>
                      )}
                    </div>

                    {/* Content area */}
                    <div style={{ padding: '16px', maxHeight: '500px', overflowY: 'auto' }}>
                      {/* Transcript */}
                      {activeSection === 'transcript' && rec.transcriptionText && (
                        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8', color: '#e5e7eb' }}>
                          {rec.transcriptionText.split('\n').map((line, i) => {
                            const match = line.match(/^\[(.+?)\]:\s*(.*)$/);
                            if (match) {
                              const speaker = match[1];
                              const text = match[2];
                              const isProvider = speaker.toLowerCase().includes('provider') || speaker === 'Speaker A';
                              return (
                                <div key={i} style={{ marginBottom: '8px' }}>
                                  <span style={{
                                    backgroundColor: isProvider ? '#059669' : '#2563EB',
                                    color: '#fff', padding: '1px 8px', borderRadius: '4px',
                                    fontSize: '11px', fontWeight: '600', marginRight: '8px'
                                  }}>
                                    {speaker}
                                  </span>
                                  <span style={{ color: '#e5e7eb' }}>{text}</span>
                                </div>
                              );
                            }
                            return <div key={i} style={{ color: '#d1d5db' }}>{line}</div>;
                          })}
                        </div>
                      )}

                      {/* AI Clinical Note */}
                      {activeSection === 'note' && rec.noteSuggestion && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                              Template: {rec.noteSuggestion.template} • Confidence: {Math.round((rec.noteSuggestion.confidence || 0) * 100)}%
                            </span>
                          </div>
                          {rec.noteSuggestion.sections && Object.entries(rec.noteSuggestion.sections).map(([key, value]) => (
                            <div key={key} style={{ marginBottom: '16px' }}>
                              <h4 style={{ color: '#D4A017', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>
                                {key}
                              </h4>
                              <p style={{ color: '#e5e7eb', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                {value}
                              </p>
                            </div>
                          ))}
                          {rec.noteSuggestion.warnings && rec.noteSuggestion.warnings.length > 0 && (
                            <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '6px' }}>
                              <p style={{ color: '#EAB308', fontSize: '12px', fontWeight: '600', margin: '0 0 4px' }}>Warnings:</p>
                              {rec.noteSuggestion.warnings.map((w, i) => (
                                <p key={i} style={{ color: '#FDE68A', fontSize: '12px', margin: '2px 0' }}>{w}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Session Summary */}
                      {activeSection === 'summary' && rec.summary && (
                        <div>
                          {rec.summary.providerSummary && (
                            <div style={{ marginBottom: '20px' }}>
                              <h4 style={{ color: '#D4A017', fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>PROVIDER SUMMARY</h4>
                              <p style={{ color: '#e5e7eb', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{rec.summary.providerSummary}</p>
                            </div>
                          )}
                          {rec.summary.keyTopics && rec.summary.keyTopics.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                              <h4 style={{ color: '#D4A017', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>KEY TOPICS</h4>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {rec.summary.keyTopics.map((topic, i) => (
                                  <span key={i} style={{ backgroundColor: '#374151', color: '#e5e7eb', padding: '3px 10px', borderRadius: '12px', fontSize: '12px' }}>{topic}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {rec.summary.interventionsUsed && rec.summary.interventionsUsed.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                              <h4 style={{ color: '#D4A017', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>INTERVENTIONS USED</h4>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {rec.summary.interventionsUsed.map((item, i) => (
                                  <span key={i} style={{ backgroundColor: '#1e3a5f', color: '#93c5fd', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', border: '1px solid #3b82f6' }}>{item}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {rec.summary.riskFlags && rec.summary.riskFlags.length > 0 && (
                            <div style={{ marginBottom: '16px', padding: '10px 12px', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px' }}>
                              <h4 style={{ color: '#EF4444', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>RISK FLAGS</h4>
                              {rec.summary.riskFlags.map((flag, i) => (
                                <p key={i} style={{ color: '#FCA5A5', fontSize: '12px', margin: '2px 0' }}>{flag}</p>
                              ))}
                            </div>
                          )}
                          {rec.summary.nextSessionFocus && rec.summary.nextSessionFocus.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                              <h4 style={{ color: '#D4A017', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>NEXT SESSION FOCUS</h4>
                              <ul style={{ color: '#e5e7eb', fontSize: '13px', margin: 0, paddingLeft: '20px' }}>
                                {rec.summary.nextSessionFocus.map((item, i) => (
                                  <li key={i} style={{ marginBottom: '4px' }}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NOTE_TEMPLATE_SECTIONS: Record<string, { key: string; label: string; placeholder: string }[]> = {
  SOAP: [
    { key: 'subjective', label: 'Subjective Complaint', placeholder: "Client's self-reported feelings, emotions, concerns, and symptoms..." },
    { key: 'objective', label: 'Objective Findings', placeholder: 'Clinical observations: appearance, behavior, affect, speech, eye contact...' },
    { key: 'assessment', label: 'Assessment of Progress', placeholder: 'Diagnosis, clinical impressions, progress toward goals...' },
    { key: 'plan', label: 'Plans for Next Session', placeholder: 'Treatment continuation, homework, referrals, next session focus...' },
  ],
  DAP: [
    { key: 'data', label: 'Data', placeholder: 'Session facts: what the client reported, what you observed, interventions used...' },
    { key: 'assessment', label: 'Assessment', placeholder: 'Clinical interpretation of the data, progress, diagnostic impressions...' },
    { key: 'plan', label: 'Plan', placeholder: 'Next steps, homework, referrals, treatment adjustments...' },
  ],
  BIRP: [
    { key: 'behavior', label: 'Behavior', placeholder: 'Observable client behaviors during session: mood, affect, engagement...' },
    { key: 'intervention', label: 'Intervention', placeholder: 'Therapeutic techniques and interventions used during the session...' },
    { key: 'response', label: 'Response', placeholder: "Client's response to interventions: receptivity, insight, resistance..." },
    { key: 'plan', label: 'Plan', placeholder: 'Homework, follow-up actions, next session goals...' },
  ],
  Progress: [
    { key: 'progress', label: 'Progress Summary', placeholder: 'Overall progress toward treatment goals since last session...' },
    { key: 'goals', label: 'Treatment Goals Addressed', placeholder: 'Which goals were worked on, current status of each...' },
    { key: 'barriers', label: 'Barriers to Progress', placeholder: 'Obstacles, challenges, or setbacks encountered...' },
    { key: 'nextSteps', label: 'Next Steps', placeholder: 'Planned interventions, homework, session frequency adjustments...' },
  ],
  Initial: [
    { key: 'presentingProblem', label: 'Presenting Problem', placeholder: 'Chief complaint, reason for seeking treatment, symptom onset and duration...' },
    { key: 'history', label: 'Relevant History', placeholder: 'Mental health history, medical history, family history, substance use...' },
    { key: 'mentalStatus', label: 'Mental Status Exam', placeholder: 'Appearance, behavior, mood, affect, thought process, cognition, insight, judgment...' },
    { key: 'diagnosis', label: 'Diagnostic Impression', placeholder: 'ICD-10 codes, differential diagnosis, clinical rationale...' },
    { key: 'recommendations', label: 'Recommendations', placeholder: 'Treatment recommendations, frequency, referrals, medication considerations...' },
  ],
};

const NOTE_TEMPLATE_LABELS: Record<string, string> = {
  SOAP: 'SOAP Note',
  DAP: 'DAP Note',
  BIRP: 'BIRP Note',
  Progress: 'Progress Note',
  Initial: 'Initial Assessment',
};

// Build flat content from section data for Firestore storage
function buildNoteContent(template: string, sections: Record<string, string>): string {
  const fields = NOTE_TEMPLATE_SECTIONS[template];
  if (!fields) return Object.values(sections).filter(Boolean).join('\n\n');
  return fields
    .map(f => `${f.label}:\n${sections[f.key] || ''}`)
    .join('\n\n');
}

// Parse flat content back into section data
function parseNoteContent(template: string, content: string): Record<string, string> {
  const fields = NOTE_TEMPLATE_SECTIONS[template];
  if (!fields) return { content };
  const sections: Record<string, string> = {};
  fields.forEach((f, i) => {
    const label = f.label + ':';
    const nextLabel = i < fields.length - 1 ? fields[i + 1].label + ':' : null;
    const startIdx = content.indexOf(label);
    if (startIdx === -1) return;
    const valueStart = startIdx + label.length;
    const endIdx = nextLabel ? content.indexOf(nextLabel, valueStart) : content.length;
    sections[f.key] = content.substring(valueStart, endIdx === -1 ? content.length : endIdx).trim();
  });
  return sections;
}

const NotesTab: React.FC<{ client: Patient }> = ({ client }) => {
  const { user, roles } = useAuth();
  const toast = useToast();
  const providerId = user?.uid || '';
  const isAdminOrDirector = roles.includes('system_admin') || roles.includes('clinical_director');
  const [notes, setNotes] = useState<ClinicalNote[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [sectionData, setSectionData] = useState<Record<string, Record<string, string>>>({});
  const [noteDate, setNoteDate] = useState(new Date().toISOString().split('T')[0]);
  const [noteTitle, setNoteTitle] = useState('');

  // Edit mode
  const [editingNote, setEditingNote] = useState<ClinicalNote | null>(null);
  const [editSections, setEditSections] = useState<Record<string, string>>({});

  // Psychotherapy notes (separate from clinical record per HIPAA 45 CFR 164.501)
  const [psychNotes, setPsychNotes] = useState<PsychotherapyNote[]>([]);
  const [psychExpanded, setPsychExpanded] = useState(false);
  const [psychContent, setPsychContent] = useState('');
  const [psychSaving, setPsychSaving] = useState(false);
  const [editingPsychNote, setEditingPsychNote] = useState<PsychotherapyNote | null>(null);
  const [editPsychContent, setEditPsychContent] = useState('');
  const [editTemplate, setEditTemplate] = useState('');

  useEffect(() => {
    if (!client.id) return;
    // Clinical notes already query without providerId filter
    const unsubscribe = FirebaseClinicalNotesService.subscribeToPatientNotes(
      providerId, client.id, (data) => setNotes(data)
    );
    return () => unsubscribe();
  }, [client.id, providerId]);

  // Subscribe to psychotherapy notes (separate collection)
  useEffect(() => {
    if (!client.id) return;
    const unsubscribe = isAdminOrDirector
      ? FirebasePsychotherapyNotesService.subscribeToAllPatientNotes(client.id, (data) => setPsychNotes(data))
      : FirebasePsychotherapyNotesService.subscribeToPatientNotes(providerId, client.id, (data) => setPsychNotes(data));
    return () => unsubscribe();
  }, [client.id, providerId, isAdminOrDirector]);

  const handleSavePsychDraft = async () => {
    if (!psychContent.trim()) return;
    setPsychSaving(true);
    try {
      await FirebasePsychotherapyNotesService.createNote({
        sessionId: '',
        patientId: client.id,
        providerId,
        content: psychContent.trim(),
        status: 'draft',
      });
      setPsychContent('');
      toast.success('Saved', 'Psychotherapy note saved as draft.');
    } catch (error: any) {
      toast.error('Save Failed', error?.message || 'Please try again.');
    } finally {
      setPsychSaving(false);
    }
  };

  const handleSignPsychNote = async (noteId: string) => {
    try {
      await FirebasePsychotherapyNotesService.signNote(noteId, providerId);
      toast.success('Signed', 'Psychotherapy note signed. It is now immutable.');
    } catch (error: any) {
      toast.error('Sign Failed', error?.message || 'Please try again.');
    }
  };

  const handleUpdatePsychNote = async () => {
    if (!editingPsychNote || !editPsychContent.trim()) return;
    setPsychSaving(true);
    try {
      await FirebasePsychotherapyNotesService.updateNote(
        editingPsychNote.id, editPsychContent.trim(), providerId
      );
      setEditingPsychNote(null);
      setEditPsychContent('');
      toast.success('Updated', 'Psychotherapy note updated.');
    } catch (error: any) {
      toast.error('Update Failed', error?.message || 'Please try again.');
    } finally {
      setPsychSaving(false);
    }
  };

  const toggleTemplate = (key: string) => {
    setExpandedTemplate(prev => prev === key ? null : key);
  };

  const updateSection = (templateKey: string, sectionKey: string, value: string) => {
    setSectionData(prev => ({
      ...prev,
      [templateKey]: { ...(prev[templateKey] || {}), [sectionKey]: value }
    }));
  };

  const hasSectionContent = (templateKey: string) => {
    const data = sectionData[templateKey];
    return data && Object.values(data).some(v => v.trim());
  };

  const handleSaveDraft = async (templateKey: string) => {
    const sectionContent = buildNoteContent(templateKey, sectionData[templateKey] || {});
    if (!sectionContent.trim()) return;
    const content = noteTitle ? `${noteTitle}\n\n${sectionContent}` : sectionContent;
    setSaving(true);
    try {
      await FirebaseClinicalNotesService.createNote({
        sessionId: '',
        patientId: client.id,
        providerId,
        template: templateKey as NoteTemplate,
        content,
        wasAiAssisted: false,
        providerEdited: true,
        version: 1,
        editHistory: [],
        status: 'draft',
      });
      setSectionData(prev => ({ ...prev, [templateKey]: {} }));
      setNoteTitle('');
      setExpandedTemplate(null);
      setShowForm(false);
      toast.success('Draft Saved', 'Note saved as draft.');
    } catch (error: any) {
      toast.error('Save Failed', error?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignAndFinalize = async (templateKey: string) => {
    const sectionContent = buildNoteContent(templateKey, sectionData[templateKey] || {});
    if (!sectionContent.trim()) return;
    const content = noteTitle ? `${noteTitle}\n\n${sectionContent}` : sectionContent;
    setSaving(true);
    try {
      const noteId = await FirebaseClinicalNotesService.createNote({
        sessionId: '',
        patientId: client.id,
        providerId,
        template: templateKey as NoteTemplate,
        content,
        wasAiAssisted: false,
        providerEdited: true,
        version: 1,
        editHistory: [],
        status: 'final',
      });
      await FirebaseClinicalNotesService.signNote(noteId, providerId);
      setSectionData(prev => ({ ...prev, [templateKey]: {} }));
      setNoteTitle('');
      setExpandedTemplate(null);
      setShowForm(false);
      toast.success('Note Signed', 'Clinical note signed and finalized.');
    } catch (error: any) {
      toast.error('Sign Failed', error?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEditNote = (note: ClinicalNote) => {
    setEditingNote(note);
    setEditTemplate(note.template);
    setEditSections(parseNoteContent(note.template, note.content));
    setShowForm(false);
  };

  const handleUpdateNote = async () => {
    if (!editingNote) return;
    const content = buildNoteContent(editTemplate, editSections);
    if (!content.trim()) return;
    setSaving(true);
    try {
      await FirebaseClinicalNotesService.updateNote(editingNote.id, { content }, providerId, 'manual-edit');
      setEditingNote(null);
      setEditSections({});
      toast.success('Note Updated', 'Clinical note updated.');
    } catch (error: any) {
      toast.error('Update Failed', error?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignNote = async (noteId: string) => {
    try {
      await FirebaseClinicalNotesService.signNote(noteId, providerId);
      toast.success('Note Signed', 'Clinical note has been signed and finalized.');
    } catch (error: any) {
      toast.error('Sign Failed', error?.message || 'Please try again.');
    }
  };

  const getStatusColor = (status: NoteStatus) => {
    switch (status) {
      case 'draft': return '#f59e0b';
      case 'final': return '#3b82f6';
      case 'signed': return '#10b981';
      default: return '#6b7280';
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #6CA6C1',
    background: '#28527A',
    color: '#e5e7eb',
    fontSize: '14px',
  };

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #6CA6C1'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#F7E9A0', margin: 0 }}>
            Clinical Notes
          </h3>
          <input
            type="date"
            value={noteDate}
            onChange={(e) => setNoteDate(e.target.value)}
            style={{ ...inputStyle, width: 'auto', colorScheme: 'dark' }}
          />
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingNote(null);
            setExpandedTemplate('SOAP');
            if (!showForm) setNoteTitle(`Session #${notes.length + 1}`);
          }}
          style={{
            background: '#58A6FF', border: 'none', borderRadius: '6px',
            padding: '8px 16px', color: 'white', cursor: 'pointer',
            fontSize: '14px', fontWeight: '500'
          }}
        >
          {showForm ? 'Cancel' : '+ Add Note'}
        </button>
      </div>

      {/* Note Editor — template dropdown + all fields visible */}
      {showForm && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          backgroundColor: '#16325B', borderRadius: '8px',
          border: '1px solid #6CA6C1', padding: '24px'
        }}>
          {/* Title + Template + Load Last Note row */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', color: '#87CEEB', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Title
              </label>
              <input
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="e.g., Session #1"
                style={{ ...inputStyle }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#87CEEB', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Template
              </label>
              <select
              value={expandedTemplate || 'SOAP'}
              onChange={(e) => setExpandedTemplate(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: '280px' }}
            >
              {Object.entries(NOTE_TEMPLATE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            </div>
            <button
              onClick={() => {
                const templateKey = expandedTemplate || 'SOAP';
                const lastNote = notes
                  .filter(n => n.template === templateKey)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
                if (lastNote) {
                  const parsed = parseNoteContent(templateKey, lastNote.content);
                  setSectionData(prev => ({ ...prev, [templateKey]: parsed }));
                  toast.success('Loaded', `Pre-filled from last ${NOTE_TEMPLATE_LABELS[templateKey] || templateKey} note.`);
                } else {
                  // Try loading from any template if none match
                  const anyLast = notes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
                  if (anyLast) {
                    const parsed = parseNoteContent(anyLast.template, anyLast.content);
                    setSectionData(prev => ({ ...prev, [templateKey]: parsed }));
                    toast.success('Loaded', `Pre-filled from last note (${NOTE_TEMPLATE_LABELS[anyLast.template] || anyLast.template}).`);
                  } else {
                    toast.info('No Notes', 'No previous notes found to load from.');
                  }
                }
              }}
              disabled={notes.length === 0}
              style={{
                background: notes.length > 0 ? '#374151' : '#1f2937',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                padding: '10px 16px',
                color: notes.length > 0 ? '#f9fafb' : '#6b7280',
                cursor: notes.length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
              }}
            >
              Load last note
            </button>
          </div>

          {/* All section fields visible at once */}
          {(NOTE_TEMPLATE_SECTIONS[expandedTemplate || 'SOAP'] || []).map(field => (
            <div key={field.key} style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <label style={{ color: '#87CEEB', fontSize: '14px', fontWeight: '600' }}>
                  {field.label}
                </label>
                <VoiceInputButton
                  size={16}
                  onResult={(text) => updateSection(
                    expandedTemplate || 'SOAP',
                    field.key,
                    ((sectionData[expandedTemplate || 'SOAP'] || {})[field.key] || '') + (((sectionData[expandedTemplate || 'SOAP'] || {})[field.key] || '').length ? ' ' : '') + text
                  )}
                  style={{ marginTop: 0 }}
                />
              </div>
              <textarea
                value={(sectionData[expandedTemplate || 'SOAP'] || {})[field.key] || ''}
                onChange={(e) => updateSection(expandedTemplate || 'SOAP', field.key, e.target.value)}
                placeholder={field.placeholder}
                style={{
                  ...inputStyle,
                  minHeight: '100px',
                  resize: 'vertical',
                  lineHeight: '1.6',
                }}
              />
            </div>
          ))}

          {/* Save buttons at the bottom */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button
              onClick={() => handleSaveDraft(expandedTemplate || 'SOAP')}
              disabled={!hasSectionContent(expandedTemplate || 'SOAP') || saving}
              style={{
                background: hasSectionContent(expandedTemplate || 'SOAP') && !saving ? '#3b82f6' : '#6b7280',
                border: 'none', borderRadius: '6px', padding: '10px 24px',
                color: 'white', cursor: hasSectionContent(expandedTemplate || 'SOAP') && !saving ? 'pointer' : 'not-allowed',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={() => handleSignAndFinalize(expandedTemplate || 'SOAP')}
              disabled={!hasSectionContent(expandedTemplate || 'SOAP') || saving}
              style={{
                background: hasSectionContent(expandedTemplate || 'SOAP') && !saving ? '#10b981' : '#6b7280',
                border: 'none', borderRadius: '6px', padding: '10px 24px',
                color: 'white', cursor: hasSectionContent(expandedTemplate || 'SOAP') && !saving ? 'pointer' : 'not-allowed',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              Sign & Finalize
            </button>
          </div>

          {/* Psychotherapy Note section — separate from clinical record per HIPAA */}
          <div style={{
            marginTop: '24px', paddingTop: '20px',
            borderTop: '1px solid #2A5470'
          }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setPsychExpanded(!psychExpanded)}
            >
              <div>
                <span style={{ fontWeight: '600', fontSize: '16px', color: '#f9fafb' }}>
                  Psychotherapy Note
                </span>
                <p style={{ color: '#d1d5db', fontSize: '12px', margin: '4px 0 0' }}>
                  Kept separate from the client record per HIPAA (45 CFR 164.501).
                  {psychNotes.length > 0 && ` (${psychNotes.length} note${psychNotes.length !== 1 ? 's' : ''})`}
                </p>
              </div>
              <span style={{
                background: '#78350f', padding: '4px 10px', borderRadius: '10px',
                fontSize: '12px', color: '#fbbf24', fontWeight: '500'
              }}>
                {psychExpanded ? '▾ Collapse' : '▸ Expand'}
              </span>
            </div>

            {psychExpanded && (
              <div style={{ marginTop: '16px' }}>
                {/* Compose new psychotherapy note */}
                {!editingPsychNote && (
                  <div style={{
                    background: '#0f1f35', borderRadius: '6px', padding: '16px',
                    border: '1px dashed #78350f', marginBottom: '16px'
                  }}>
                    <label style={{ display: 'block', color: '#fbbf24', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                      Personal observations (not part of the clinical record)
                    </label>
                    <textarea
                      value={psychContent}
                      onChange={(e) => setPsychContent(e.target.value)}
                      placeholder="Document your personal observations, hypotheses, and impressions from this session. These notes are for your eyes only and are stored separately from the clinical record."
                      style={{
                        ...inputStyle,
                        minHeight: '120px',
                        resize: 'vertical',
                        lineHeight: '1.6',
                        borderColor: '#78350f',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px' }}>
                      <button
                        onClick={handleSavePsychDraft}
                        disabled={!psychContent.trim() || psychSaving}
                        style={{
                          background: psychContent.trim() && !psychSaving ? '#b45309' : '#6b7280',
                          border: 'none', borderRadius: '6px', padding: '8px 20px',
                          color: 'white', cursor: psychContent.trim() && !psychSaving ? 'pointer' : 'not-allowed',
                          fontSize: '13px', fontWeight: '500'
                        }}
                      >
                        {psychSaving ? 'Saving...' : 'Save Draft'}
                      </button>
                      <button
                        onClick={async () => {
                          if (!psychContent.trim()) return;
                          setPsychSaving(true);
                          try {
                            const noteId = await FirebasePsychotherapyNotesService.createNote({
                              sessionId: '',
                              patientId: client.id,
                              providerId,
                              content: psychContent.trim(),
                              status: 'draft',
                            });
                            await FirebasePsychotherapyNotesService.signNote(noteId, providerId);
                            setPsychContent('');
                            toast.success('Signed', 'Psychotherapy note signed and finalized.');
                          } catch (error: any) {
                            toast.error('Failed', error?.message || 'Please try again.');
                          } finally {
                            setPsychSaving(false);
                          }
                        }}
                        disabled={!psychContent.trim() || psychSaving}
                        style={{
                          background: psychContent.trim() && !psychSaving ? '#059669' : '#6b7280',
                          border: 'none', borderRadius: '6px', padding: '8px 20px',
                          color: 'white', cursor: psychContent.trim() && !psychSaving ? 'pointer' : 'not-allowed',
                          fontSize: '13px', fontWeight: '500'
                        }}
                      >
                        Sign & Finalize
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit existing draft */}
                {editingPsychNote && (
                  <div style={{
                    background: '#0f1f35', borderRadius: '6px', padding: '16px',
                    border: '1px solid #f59e0b', marginBottom: '16px'
                  }}>
                    <label style={{ display: 'block', color: '#fbbf24', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                      Editing psychotherapy note
                    </label>
                    <textarea
                      value={editPsychContent}
                      onChange={(e) => setEditPsychContent(e.target.value)}
                      style={{
                        ...inputStyle,
                        minHeight: '120px',
                        resize: 'vertical',
                        lineHeight: '1.6',
                        borderColor: '#f59e0b',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px' }}>
                      <button
                        onClick={() => { setEditingPsychNote(null); setEditPsychContent(''); }}
                        style={{
                          background: '#6b7280', border: 'none', borderRadius: '6px',
                          padding: '8px 16px', color: 'white', cursor: 'pointer', fontSize: '13px'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdatePsychNote}
                        disabled={!editPsychContent.trim() || psychSaving}
                        style={{
                          background: editPsychContent.trim() && !psychSaving ? '#b45309' : '#6b7280',
                          border: 'none', borderRadius: '6px', padding: '8px 20px',
                          color: 'white', cursor: editPsychContent.trim() && !psychSaving ? 'pointer' : 'not-allowed',
                          fontSize: '13px', fontWeight: '500'
                        }}
                      >
                        {psychSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Existing psychotherapy notes list */}
                {psychNotes.length === 0 ? (
                  <p style={{ color: '#d1d5db', fontStyle: 'italic', fontSize: '13px', margin: '8px 0 0' }}>
                    No psychotherapy notes yet for this client.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {psychNotes.map(pn => (
                      <div key={pn.id} style={{
                        background: '#0f1f35', borderRadius: '6px', padding: '14px',
                        border: `1px solid ${pn.status === 'signed' ? '#059669' : '#78350f'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{
                              background: pn.status === 'signed' ? '#059669' : '#b45309',
                              padding: '3px 8px', borderRadius: '4px', fontSize: '11px',
                              fontWeight: '500', color: 'white', textTransform: 'capitalize'
                            }}>
                              {pn.status === 'signed' ? 'Signed (Immutable)' : 'Draft'}
                            </span>
                            <span style={{ color: '#d1d5db', fontSize: '12px' }}>
                              {new Date(pn.createdAt).toLocaleDateString()} {new Date(pn.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {pn.status !== 'signed' && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => {
                                  setEditingPsychNote(pn);
                                  setEditPsychContent(pn.content);
                                }}
                                style={{
                                  background: '#f59e0b', border: 'none', borderRadius: '4px',
                                  padding: '4px 10px', color: 'white', cursor: 'pointer', fontSize: '11px'
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleSignPsychNote(pn.id)}
                                style={{
                                  background: '#059669', border: 'none', borderRadius: '4px',
                                  padding: '4px 10px', color: 'white', cursor: 'pointer', fontSize: '11px'
                                }}
                              >
                                Sign
                              </button>
                            </div>
                          )}
                        </div>
                        <pre style={{
                          color: '#e5e7eb', fontSize: '13px', whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                          margin: 0, fontFamily: 'inherit', lineHeight: '1.5'
                        }}>
                          {pn.content}
                        </pre>
                        {pn.signedAt && (
                          <p style={{ color: '#059669', fontSize: '11px', margin: '8px 0 0', fontStyle: 'italic' }}>
                            Signed on {new Date(pn.signedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <p style={{ color: '#78350f', fontSize: '11px', marginTop: '12px', fontStyle: 'italic' }}>
                  These notes are stored in a separate, encrypted collection and are never shared with the client or insurance.
                  Signed notes are immutable and cannot be edited.
                </p>
              </div>
            )}
          </div>
        </div>
        </div>
        <NotesSidebar client={client} providerId={providerId} noteDate={noteDate} />
        </div>
      )}

      {/* Edit Note with section fields */}
      {editingNote && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              backgroundColor: '#16325B', padding: '20px', borderRadius: '6px',
              border: '1px solid #f59e0b'
            }}>
              <h4 style={{ color: '#F7E9A0', marginBottom: '16px' }}>
                Editing: {NOTE_TEMPLATE_LABELS[editTemplate] || editTemplate} Note (v{editingNote.version})
              </h4>
              {NOTE_TEMPLATE_SECTIONS[editTemplate] ? (
                NOTE_TEMPLATE_SECTIONS[editTemplate].map(field => (
                  <div key={field.key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', color: '#87CEEB', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                      {field.label}
                    </label>
                    <textarea
                      value={editSections[field.key] || ''}
                      onChange={(e) => setEditSections(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', lineHeight: '1.5' }}
                    />
                  </div>
                ))
              ) : (
                <textarea
                  value={Object.values(editSections).join('\n\n')}
                  onChange={(e) => setEditSections({ content: e.target.value })}
                  style={{ ...inputStyle, minHeight: '200px', resize: 'vertical', fontFamily: 'monospace', lineHeight: '1.5', marginBottom: '16px' }}
                />
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button onClick={handleUpdateNote} disabled={saving}
                  style={{ background: '#10b981', border: 'none', borderRadius: '6px', padding: '10px 20px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => { setEditingNote(null); setEditSections({}); }}
                  style={{ background: '#6b7280', border: 'none', borderRadius: '6px', padding: '10px 20px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
          <NotesSidebar client={client} providerId={providerId} noteDate={editingNote.createdAt ? new Date(editingNote.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]} />
        </div>
      )}

      {/* Existing Notes List */}
      {notes.length === 0 && !showForm ? (
        <div style={{ backgroundColor: '#16325B', padding: '16px', borderRadius: '6px', border: '1px solid #6CA6C1' }}>
          <p style={{ color: '#d1d5db', fontStyle: 'italic', margin: 0 }}>
            No clinical notes yet. Click "+ Add Note" to create one.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {notes.map(note => (
            <div key={note.id} style={{
              backgroundColor: '#16325B', padding: '16px', borderRadius: '6px',
              border: `1px solid ${note.status === 'signed' ? '#10b981' : '#6CA6C1'}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ background: '#58A6FF', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                    {note.template}
                  </span>
                  <span style={{ background: getStatusColor(note.status), padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                    {note.status}
                  </span>
                  <span style={{ color: '#d1d5db', fontSize: '12px' }}>
                    v{note.version} | {new Date(note.createdAt).toLocaleDateString()} {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {note.status !== 'signed' && (
                    <>
                      <button onClick={() => startEditNote(note)}
                        style={{ background: '#f59e0b', border: 'none', borderRadius: '4px', padding: '6px 12px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                        Edit
                      </button>
                      <button onClick={() => handleSignNote(note.id)}
                        style={{ background: '#10b981', border: 'none', borderRadius: '4px', padding: '6px 12px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                        Sign
                      </button>
                    </>
                  )}
                </div>
              </div>
              <pre style={{
                color: '#e5e7eb', fontSize: '14px', whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                margin: '8px 0 0', fontFamily: 'inherit', lineHeight: '1.5',
                background: '#0f1f35', padding: '12px', borderRadius: '4px'
              }}>
                {note.content}
              </pre>
              {note.signedBy && (
                <p style={{ color: '#10b981', fontSize: '12px', margin: '8px 0 0', fontStyle: 'italic' }}>
                  Signed on {note.signedAt ? new Date(note.signedAt).toLocaleString() : 'N/A'}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <p style={{ color: '#d1d5db', fontSize: '12px', marginTop: '16px' }}>
        Clinical notes are provider-only and are NOT visible to clients (HIPAA protected).
      </p>
    </div>
  );
};

const HomeworkTab: React.FC<{ client: Patient }> = ({ client }) => {
  const { user } = useAuth();
  const toast = useToast();
  const providerId = user?.uid || '';
  const [homework, setHomework] = useState([] as HomeworkAssignment[]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!providerId || !client.id) return;
    const unsub = FirebaseHomeworkService.subscribeToProviderHomework(
      providerId, client.id, (items) => setHomework(items)
    );
    return () => unsub();
  }, [providerId, client.id]);

  const handleAssign = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await FirebaseHomeworkService.assignHomework({
        title: title.trim(),
        description: description.trim(),
        dueDate,
        assignedBy: user?.displayName || 'Provider',
        status: 'pending',
        clientId: client.id,
        providerId,
      });
      setTitle('');
      setDescription('');
      setDueDate('');
      setShowForm(false);
      toast.success('Homework Assigned', `"${title.trim()}" sent to ${client.firstName}.`);
    } catch (error) {
      console.error('Failed to assign homework:', error);
      toast.error('Failed', 'Could not assign homework.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'overdue': return '#EF4444';
      default: return '#F59E0B';
    }
  };

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '24px',
      border: '1px solid #6CA6C1'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#F7E9A0', margin: 0 }}>
          Homework Assignments
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            background: showForm ? '#6b7280' : '#58A6FF',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {showForm ? 'Cancel' : 'Assign Homework'}
        </button>
      </div>

      {showForm && (
        <div style={{
          backgroundColor: '#16325B',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label htmlFor="hw-title" style={{ color: '#e5e7eb', fontSize: '14px', display: 'block', marginBottom: '4px' }}>Title</label>
              <input
                id="hw-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Daily Mood Tracking"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: '#28527A',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label htmlFor="hw-desc" style={{ color: '#e5e7eb', fontSize: '14px', display: 'block', marginBottom: '4px' }}>Description</label>
              <textarea
                id="hw-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what the client should do..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: '#28527A',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label htmlFor="hw-due" style={{ color: '#e5e7eb', fontSize: '14px', display: 'block', marginBottom: '4px' }}>Due Date</label>
              <input
                id="hw-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{
                  padding: '8px 12px',
                  background: '#28527A',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#f9fafb',
                  fontSize: '14px'
                }}
              />
            </div>
            <button
              onClick={handleAssign}
              disabled={!title.trim() || submitting}
              style={{
                background: !title.trim() || submitting ? '#4b5563' : '#10B981',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 20px',
                color: 'white',
                cursor: !title.trim() || submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                alignSelf: 'flex-start'
              }}
            >
              {submitting ? 'Assigning...' : 'Assign to Client'}
            </button>
          </div>
        </div>
      )}

      {homework.length === 0 ? (
        <div style={{
          backgroundColor: '#16325B',
          padding: '24px',
          borderRadius: '6px',
          border: '1px solid #6CA6C1',
          textAlign: 'center'
        }}>
          <p style={{ color: '#d1d5db', fontStyle: 'italic', margin: 0 }}>
            No homework assignments yet. Click "Assign Homework" to get started.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {homework.map((hw) => (
            <div key={hw.id} style={{
              backgroundColor: '#16325B',
              padding: '16px',
              borderRadius: '6px',
              border: '1px solid #6CA6C1'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>{hw.title}</h4>
                <span style={{
                  background: getStatusColor(hw.status),
                  color: 'white',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  textTransform: 'capitalize'
                }}>
                  {hw.status}
                </span>
              </div>
              {hw.description && (
                <p style={{ color: '#d1d5db', fontSize: '14px', margin: '0 0 8px 0' }}>{hw.description}</p>
              )}
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#d1d5db' }}>
                {hw.dueDate && <span>Due: {new Date(hw.dueDate).toLocaleDateString()}</span>}
                <span>Assigned: {hw.createdAt.toLocaleDateString()}</span>
              </div>
              {hw.status === 'completed' && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '4px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <div style={{ fontSize: '12px', color: '#10B981', marginBottom: '4px' }}>
                    Completed {hw.completedAt ? hw.completedAt.toLocaleDateString() : ''}
                  </div>
                  {hw.clientNotes && (
                    <p style={{ color: '#d1d5db', fontSize: '13px', margin: 0 }}>
                      Client notes: {hw.clientNotes}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ClientProfile: React.FC<ClientProfileProps> = ({ client, onBack, onStartSession, onScheduleAppointment, onMessageClient }) => {
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const { user, roles } = useAuth();
  const { createConversation } = useMessaging();
  const isAdminStaffOnly = roles.includes('administrative_staff') && !roles.includes('provider') && !roles.includes('clinical_director') && !roles.includes('system_admin');
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [activeTreatmentTab, setActiveTreatmentTab] = useState<TreatmentSubTab>('treatment-plan');

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTab, setEditTab] = useState<'clientInfo' | 'billing' | 'contacts' | 'clientPortal'>('clientInfo');
  const [editFormData, setEditFormData] = useState<Patient>(client);

  const handleResetClinicalData = async () => {
    const confirmed = await confirm({ title: 'Clear Clinical Data', message: 'This will clear demo clinical data (medical history, allergies, medications). This cannot be undone. Continue?', confirmLabel: 'Clear Data', variant: 'danger' });
    if (!confirmed) {
      return;
    }
    try {
      await FirebasePatientService.updatePatient(client.id, {
        medicalHistory: '',
        allergies: '',
        currentMedications: '',
        presentingConcern: '',
        chiefComplaint: '',
      });
      toast.success('Data Cleared', 'Clinical demo data has been reset.');
    } catch (error) {
      console.error('Failed to reset clinical data:', error);
      toast.error('Reset Failed', 'Could not clear clinical data.');
    }
  };

  const allTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'treatment', label: 'Treatment', clinicalOnly: true },
    { id: 'measures', label: 'Measures', clinicalOnly: true },
    { id: 'files', label: 'Files' },
    { id: 'forms', label: 'Forms' },
    { id: 'billing', label: 'Billing' }
  ];
  const tabs = isAdminStaffOnly ? allTabs.filter(t => !t.clinicalOnly) : allTabs;

  const treatmentSubTabs = [
    { id: 'treatment-plan', label: 'Treatment Plan' },
    { id: 'diagnosis', label: 'Diagnosis' },
    { id: 'prescriptions', label: 'Prescriptions' },
    { id: 'sessions', label: 'Sessions' },
    { id: 'notes', label: 'Notes' },
    { id: 'homework', label: 'Homework' }
  ];

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#16325B',
      minHeight: '100vh',
      color: '#f9fafb'
    }}>
      {ConfirmDialog}
      {/* Header Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <button
              onClick={onBack}
              style={{
                background: 'none',
                border: 'none',
                color: '#F7E9A0',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px'
              }}
            >
              ←
            </button>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#F7E9A0',
              margin: '0'
            }}>
{`${client.firstName} ${client.lastName}`}
            </h1>
          </div>
          <div style={{
            fontSize: '14px',
            color: '#d1d5db',
            marginLeft: '34px'
          }}>
            Adult | {client.dateOfBirth?.split('-').length === 3 ? `${client.dateOfBirth.split('-')[1]}/${client.dateOfBirth.split('-')[2]}/${client.dateOfBirth.split('-')[0]}` : new Date(client.dateOfBirth).toLocaleDateString('en-US')} |
            <button
              onClick={onScheduleAppointment}
              style={{
                background: 'none',
                border: 'none',
                color: '#58A6FF',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginLeft: '4px'
              }}
            >
              Schedule appointment
            </button> |
            <button
              onClick={() => {
                setEditFormData(client);
                setEditTab('clientInfo');
                setIsEditMode(true);
                setTimeout(() => {
                  document.getElementById('edit-form-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#58A6FF',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginLeft: '4px'
              }}
            >
              Edit
            </button>{!isAdminStaffOnly && (<> |
            <button
              onClick={handleResetClinicalData}
              style={{
                background: 'none',
                border: 'none',
                color: '#f87171',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginLeft: '4px'
              }}>
              Reset Clinical Data
            </button></>)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={async () => {
              if (onMessageClient) {
                onMessageClient(client);
              } else {
                // Create/find conversation and navigate to messages
                try {
                  await createConversation({
                    providerId: user?.uid || '',
                    providerName: user?.displayName || 'Provider',
                    clientId: client.id,
                    clientName: `${client.firstName} ${client.lastName}`,
                  });
                  toast.success('Conversation Ready', `You can now message ${client.firstName}.`);
                } catch (error) {
                  console.error('Failed to create conversation:', error);
                  toast.error('Error', 'Could not start conversation.');
                }
              }
            }}
            style={{
              background: '#58A6FF',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Message Client
          </button>
          <button onClick={() => alert('Referral sending coming soon')} style={{
            background: '#6b7280',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            cursor: 'pointer'
          }}>
            Send Referrals
          </button>
          <button style={{
            background: '#6b7280',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            cursor: 'pointer'
          }}>
            Share
          </button>
          <button style={{
            background: '#6b7280',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            cursor: 'pointer'
          }}>
            Upload
          </button>
        </div>
      </div>

      {/* Prospective client notification (if applicable) */}
      {client.status === 'inactive' && (
        <div style={{
          background: '#1E40AF',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: '14px' }}>
            Prospective clients have limited appointment request access. Make them active to give full access.
          </span>
          <button style={{
            background: 'none',
            border: 'none',
            color: '#f9fafb',
            cursor: 'pointer',
            fontSize: '18px'
          }}>
            ×
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        borderBottom: '1px solid #6CA6C1',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '0' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #58A6FF' : '2px solid transparent',
                padding: '12px 24px',
                color: activeTab === tab.id ? '#58A6FF' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
          {/* Administrative Note button — hidden until feature is built */}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Left Content */}
        <div style={{ flex: '1' }}>
          {activeTab === 'overview' && <OverviewTab client={client} onNavigateTab={(tab) => setActiveTab(tab as TabType)} isAdminStaffOnly={isAdminStaffOnly} />}
          {activeTab === 'treatment' && (
            <div>
              {/* Treatment Sub-tabs */}
              <div style={{
                display: 'flex',
                borderBottom: '1px solid #374151',
                marginBottom: '24px'
              }}>
                {treatmentSubTabs.map((subTab) => (
                  <button
                    key={subTab.id}
                    onClick={() => setActiveTreatmentTab(subTab.id as TreatmentSubTab)}
                    style={{
                      background: 'none',
                      border: 'none',
                      borderBottom: activeTreatmentTab === subTab.id ? '2px solid #D4A017' : '2px solid transparent',
                      padding: '8px 16px',
                      color: activeTreatmentTab === subTab.id ? '#D4A017' : '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: activeTreatmentTab === subTab.id ? '600' : '400',
                      transition: 'all 0.2s'
                    }}
                  >
                    {subTab.label}
                  </button>
                ))}
              </div>

              {/* Treatment Sub-tab Content */}
              {activeTreatmentTab === 'treatment-plan' && <TreatmentPlanTab client={client} />}
              {activeTreatmentTab === 'diagnosis' && <DiagnosisTab client={client} />}
              {activeTreatmentTab === 'prescriptions' && <PrescriptionsTab client={client} />}
              {activeTreatmentTab === 'sessions' && <SessionsTabImplementation client={client} onStartSession={onStartSession} />}
              {activeTreatmentTab === 'notes' && <NotesTab client={client} />}
              {activeTreatmentTab === 'homework' && <HomeworkTab client={client} />}
            </div>
          )}
          {activeTab === 'measures' && <MeasuresTab client={client} />}
          {activeTab === 'files' && <FilesTab client={client} />}
          {activeTab === 'forms' && <FormsTab client={client} />}
          {activeTab === 'billing' && <BillingTab client={client} />}
        </div>

        {/* Right Sidebar */}
        <ClientSidebar
          client={client}
          onEditClick={() => {
            setEditFormData(client);
            setEditTab('clientInfo');
            setIsEditMode(true);
            // Scroll to bottom where edit form is
            setTimeout(() => {
              document.getElementById('edit-form-section')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }}
          onScheduleAppointment={onScheduleAppointment}
        />
      </div>

      {/* Edit Form Section - Bottom of page */}
      {isEditMode && (
        <div
          id="edit-form-section"
          style={{
            marginTop: '32px',
            backgroundColor: '#28527A',
            borderRadius: '8px',
            border: '1px solid #6CA6C1',
            padding: '0'
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: '1px solid #6CA6C1'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#F7E9A0',
              margin: 0
            }}>
              Edit client {`${client.firstName} ${client.lastName}`}
            </h2>
            <button
              onClick={() => setIsEditMode(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f9fafb',
                cursor: 'pointer',
                fontSize: '24px',
                padding: '0 8px'
              }}
            >
              ×
            </button>
          </div>

          {/* Edit Tabs */}
          <div style={{
            display: 'flex',
            gap: '0',
            borderBottom: '1px solid #6CA6C1',
            padding: '0 20px'
          }}>
            {[
              { id: 'clientInfo', label: 'Client Info' },
              { id: 'billing', label: 'Billing and Insurance' },
              { id: 'contacts', label: 'Contacts' },
              { id: 'clientPortal', label: 'Client Portal' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setEditTab(tab.id as any)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: editTab === tab.id ? '2px solid #D4A017' : '2px solid transparent',
                  color: editTab === tab.id ? '#D4A017' : '#9ca3af',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: editTab === tab.id ? '600' : '400'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Edit Form Content */}
          <div style={{ padding: '20px' }}>
            {editTab === 'clientInfo' && (
              <div>
                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Client Status</h3>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                    Status
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      padding: '8px 12px',
                      backgroundColor: '#113F67',
                      border: '1px solid #6CA6C1',
                      borderRadius: '6px',
                      color: '#f9fafb',
                      fontSize: '14px'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="prospective">Prospective</option>
                  </select>
                </div>

                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Name</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Legal first name *
                    </label>
                    <input
                      type="text"
                      value={editFormData.firstName || ''}
                      onChange={(e) => {
                        setEditFormData({ ...editFormData, firstName: e.target.value });
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Middle name
                    </label>
                    <input
                      type="text"
                      placeholder="Optional"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Legal last name *
                    </label>
                    <input
                      type="text"
                      value={editFormData.lastName || ''}
                      onChange={(e) => {
                        setEditFormData({ ...editFormData, lastName: e.target.value });
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Contact details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      value={editFormData.dateOfBirth}
                      onChange={(e) => setEditFormData({ ...editFormData, dateOfBirth: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Address</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={editFormData.address || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        address: e.target.value
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                        City
                      </label>
                      <input
                        type="text"
                        value={editFormData.city || ''}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          city: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#113F67',
                          border: '1px solid #6CA6C1',
                          borderRadius: '6px',
                          color: '#f9fafb',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                        State
                      </label>
                      <input
                        type="text"
                        value={editFormData.state || ''}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          state: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#113F67',
                          border: '1px solid #6CA6C1',
                          borderRadius: '6px',
                          color: '#f9fafb',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                        Zip Code
                      </label>
                      <input
                        type="text"
                        value={editFormData.zipCode || ''}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          zipCode: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#113F67',
                          border: '1px solid #6CA6C1',
                          borderRadius: '6px',
                          color: '#f9fafb',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {editTab === 'billing' && (
              <div>
                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Insurance Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Insurance Provider
                    </label>
                    <input
                      type="text"
                      value={editFormData.insurance?.provider || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        insurance: { provider: e.target.value, policyNumber: editFormData.insurance?.policyNumber || '', groupNumber: editFormData.insurance?.groupNumber, subscriberName: editFormData.insurance?.subscriberName }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Subscriber Name
                    </label>
                    <input
                      type="text"
                      value={editFormData.insurance?.subscriberName || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        insurance: { ...editFormData.insurance, provider: editFormData.insurance?.provider || '', policyNumber: editFormData.insurance?.policyNumber || '', subscriberName: e.target.value }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Policy Number
                    </label>
                    <input
                      type="text"
                      value={editFormData.insurance?.policyNumber || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        insurance: { ...editFormData.insurance, provider: editFormData.insurance?.provider || '', policyNumber: e.target.value }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Group Number
                    </label>
                    <input
                      type="text"
                      value={editFormData.insurance?.groupNumber || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        insurance: { provider: editFormData.insurance?.provider || '', policyNumber: editFormData.insurance?.policyNumber || '', groupNumber: e.target.value, subscriberName: editFormData.insurance?.subscriberName }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {editTab === 'contacts' && (
              <div>
                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Emergency Contact</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Name
                    </label>
                    <input
                      type="text"
                      value={editFormData.emergencyContact?.name || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        emergencyContact: { name: e.target.value, relationship: editFormData.emergencyContact?.relationship || '', phone: editFormData.emergencyContact?.phone || '', email: editFormData.emergencyContact?.email }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Relationship
                    </label>
                    <input
                      type="text"
                      value={editFormData.emergencyContact?.relationship || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        emergencyContact: { name: editFormData.emergencyContact?.name || '', relationship: e.target.value, phone: editFormData.emergencyContact?.phone || '', email: editFormData.emergencyContact?.email }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={editFormData.emergencyContact?.phone || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        emergencyContact: { name: editFormData.emergencyContact?.name || '', relationship: editFormData.emergencyContact?.relationship || '', phone: e.target.value, email: editFormData.emergencyContact?.email }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={editFormData.emergencyContact?.email || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        emergencyContact: { name: editFormData.emergencyContact?.name || '', relationship: editFormData.emergencyContact?.relationship || '', phone: editFormData.emergencyContact?.phone || '', email: e.target.value }
                      })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#113F67',
                        border: '1px solid #6CA6C1',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {editTab === 'clientPortal' && (
              <div>
                <h3 style={{ color: '#f9fafb', marginBottom: '16px', fontSize: '18px' }}>Client Portal Settings</h3>
                <p style={{ color: '#d1d5db', fontSize: '14px' }}>
                  Client portal settings will be available once the backend is integrated.
                </p>
              </div>
            )}
          </div>

          {/* Footer with Save button */}
          <div style={{
            padding: '20px',
            borderTop: '1px solid #6CA6C1',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => setIsEditMode(false)}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  await FirebasePatientService.updatePatient(client.id, editFormData);
                  setIsEditMode(false);
                  toast.success('Client Updated', 'Client information updated successfully.');
                } catch (error) {
                  console.error('Error updating patient:', error);
                  toast.error('Update Failed', 'Failed to update client information.');
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#D4A017',
                border: 'none',
                borderRadius: '6px',
                color: '#113F67',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Overview Tab Component
// Timeline entry types for Overview
type TimelineEntryType = 'appointment' | 'note' | 'measure' | 'diagnosis' | 'prescription' | 'homework' | 'form';
interface TimelineEntry {
  id: string;
  type: TimelineEntryType;
  date: Date;
  title: string;
  subtitle?: string;
  details?: string;
  status?: string;
  statusColor?: string;
  icon: string;
  meta?: Record<string, string>;
}

const OverviewTab: React.FC<{ client: Patient; onNavigateTab?: (tab: string) => void; isAdminStaffOnly?: boolean }> = ({ client, onNavigateTab, isAdminStaffOnly }) => {
  const { user, roles } = useAuth();
  const providerId = user?.uid || '';
  const isAdminOrDirector = roles.includes('system_admin') || roles.includes('clinical_director');
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | TimelineEntryType>('all');

  useEffect(() => {
    if (!providerId || !client.id) { setLoading(false); return; }

    const allEntries: TimelineEntry[] = [];
    let loadCount = 0;
    const totalLoads = 5;
    const checkDone = () => { loadCount++; if (loadCount >= totalLoads) { setLoading(false); } };

    // 1. Appointments
    firebaseAppointmentService.getAppointments({ providerId }).then(allAppts => {
      const appts = allAppts.filter(a => a.patientId === client.id || a.patientName === `${client.firstName} ${client.lastName}`);
      return appts;
    }).then(appts => {
      for (const a of appts) {
        const cpt = SESSION_CPT[a.sessionType] || SESSION_CPT['other'];
        allEntries.push({
          id: `appt-${a.id}`,
          type: 'appointment',
          date: a.appointmentDate instanceof Date ? a.appointmentDate : new Date(a.appointmentDate),
          title: `APPOINTMENT${a.sessionType === 'initial-consultation' ? ' (Initial)' : ''}`,
          subtitle: `BILLING CODE: ${cpt.code}`,
          details: `${formatTime12(a.startTime)} - ${formatTime12(a.endTime)} · ${a.modality === 'video' ? 'Video' : 'In-person'}`,
          status: a.status,
          statusColor: a.status === 'completed' ? '#10B981' : a.status === 'scheduled' ? '#58A6FF' : '#f59e0b',
          icon: '📅',
          meta: { sessionType: a.sessionType, fee: `$${cpt.fee}` }
        });
      }
      setEntries(prev => [...prev.filter(e => e.type !== 'appointment'), ...allEntries.filter(e => e.type === 'appointment')].sort((a, b) => b.date.getTime() - a.date.getTime()));
      checkDone();
    }).catch(() => checkDone());

    // 2. Clinical Notes
    FirebaseClinicalNotesService.getNotesByPatient(providerId, client.id).then(notesData => {
      for (const n of notesData) {
        const templateLabel = NOTE_TEMPLATE_LABELS[n.template] || n.template;
        const preview = n.content.substring(0, 120).replace(/\n/g, ' ');
        allEntries.push({
          id: `note-${n.id}`,
          type: 'note',
          date: n.createdAt instanceof Date ? n.createdAt : new Date(n.createdAt),
          title: `${templateLabel.toUpperCase()} NOTE`,
          subtitle: n.status === 'signed' ? `Signed ${n.signedAt ? new Date(n.signedAt).toLocaleDateString() : ''}` : n.status,
          details: preview.length < n.content.length ? `${preview}...` : preview,
          status: n.status,
          statusColor: n.status === 'signed' ? '#10B981' : n.status === 'draft' ? '#f59e0b' : '#3b82f6',
          icon: '📝',
        });
      }
      setEntries(prev => [...prev.filter(e => e.type !== 'note'), ...allEntries.filter(e => e.type === 'note')].sort((a, b) => b.date.getTime() - a.date.getTime()));
      checkDone();
    }).catch(() => checkDone());

    // 3. Form Responses (Measures)
    FirebaseFormResponseService.getResponsesByClient(providerId, client.id).then(responses => {
      for (const r of responses) {
        const isAssessment = r.formTemplateId === 'gad7' || r.formTemplateId === 'phq9';
        let score: number | null = null;
        if (isAssessment && r.responses) {
          score = 0;
          for (const ans of r.responses) {
            if (ans.questionId === 'difficultyLevel') continue;
            const val = parseInt(ans.answer, 10);
            if (!isNaN(val)) score += val;
          }
        }
        allEntries.push({
          id: `form-${r.id}`,
          type: isAssessment ? 'measure' : 'form',
          date: r.submittedAt instanceof Date ? r.submittedAt : new Date(r.submittedAt || Date.now()),
          title: isAssessment ? `Scored measure` : 'Form submitted',
          subtitle: r.formTemplateName || r.formTemplateId,
          details: score !== null ? `Score: ${score}` : undefined,
          status: 'completed',
          statusColor: '#10B981',
          icon: isAssessment ? '📊' : '📋',
        });
      }
      setEntries(prev => [...prev.filter(e => e.type !== 'measure' && e.type !== 'form'), ...allEntries.filter(e => e.type === 'measure' || e.type === 'form')].sort((a, b) => b.date.getTime() - a.date.getTime()));
      checkDone();
    }).catch(() => checkDone());

    // 4. Diagnoses
    const diagCallback = (diags: any[]) => {
      const diagEntries: TimelineEntry[] = diags.map(d => ({
        id: `diag-${d.id}`,
        type: 'diagnosis' as TimelineEntryType,
        date: d.createdAt instanceof Date ? d.createdAt : new Date(d.createdAt),
        title: 'Diagnosis',
        subtitle: `${d.code} — ${d.name}`,
        details: d.status === 'active' ? 'Active' : d.status,
        status: d.status,
        statusColor: d.status === 'active' ? '#10B981' : '#6b7280',
        icon: '🏥',
      }));
      setEntries(prev => [...prev.filter(e => e.type !== 'diagnosis'), ...diagEntries].sort((a, b) => b.date.getTime() - a.date.getTime()));
      checkDone();
    };
    const unsubDiag = isAdminOrDirector
      ? FirebaseDiagnosisPrescriptionService.subscribeToClientDiagnoses(client.id, diagCallback)
      : FirebaseDiagnosisPrescriptionService.subscribeToDiagnoses(client.id, providerId, diagCallback);

    // 5. Homework
    FirebaseHomeworkService.getHomeworkByProvider(providerId, client.id).then(hw => {
      for (const h of hw) {
        allEntries.push({
          id: `hw-${h.id}`,
          type: 'homework',
          date: h.createdAt instanceof Date ? h.createdAt : new Date(h.createdAt || Date.now()),
          title: 'Homework assigned',
          subtitle: h.title,
          details: h.status === 'completed' ? 'Completed' : h.status === 'overdue' ? 'Overdue' : 'Pending',
          status: h.status,
          statusColor: h.status === 'completed' ? '#10B981' : h.status === 'overdue' ? '#EF4444' : '#f59e0b',
          icon: '📚',
        });
      }
      setEntries(prev => [...prev.filter(e => e.type !== 'homework'), ...allEntries.filter(e => e.type === 'homework')].sort((a, b) => b.date.getTime() - a.date.getTime()));
      checkDone();
    }).catch(() => checkDone());

    return () => { unsubDiag(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, providerId]);

  const visibleEntries = isAdminStaffOnly ? entries.filter(e => e.type === 'appointment') : entries;
  const filteredEntries = filter === 'all' ? visibleEntries : visibleEntries.filter(e => e.type === filter);

  // Group entries by date
  const grouped: Record<string, TimelineEntry[]> = {};
  for (const e of filteredEntries) {
    const key = e.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(e);
  }

  const allFilterButtons: { label: string; value: 'all' | TimelineEntryType }[] = [
    { label: 'All', value: 'all' },
    { label: 'Appointments', value: 'appointment' },
    { label: 'Notes', value: 'note' },
    { label: 'Measures', value: 'measure' },
    { label: 'Diagnoses', value: 'diagnosis' },
    { label: 'Homework', value: 'homework' },
  ];
  const filterButtons = isAdminStaffOnly
    ? allFilterButtons.filter(fb => fb.value === 'all' || fb.value === 'appointment')
    : allFilterButtons;

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {filterButtons.map(fb => (
          <button
            key={fb.value}
            onClick={() => setFilter(fb.value)}
            style={{
              background: filter === fb.value ? '#58A6FF' : '#28527A',
              border: filter === fb.value ? 'none' : '1px solid #6CA6C1',
              borderRadius: '16px',
              padding: '6px 14px',
              color: filter === fb.value ? 'white' : '#9ca3af',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: filter === fb.value ? 600 : 400,
            }}
          >
            {fb.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#d1d5db' }}>Loading activity...</div>
      ) : filteredEntries.length === 0 ? (
        <div style={{
          background: '#28527A', borderRadius: '8px', padding: '40px 20px',
          textAlign: 'center', color: '#d1d5db'
        }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
          <p style={{ fontSize: '16px', color: '#e5e7eb', marginBottom: '8px' }}>No activity yet</p>
          <p style={{ fontSize: '14px' }}>
            Items will appear here as sessions, notes, and measures are completed for {client.firstName}.
          </p>
        </div>
      ) : (
        <div>
          {Object.entries(grouped).map(([dateLabel, items]) => (
            <div key={dateLabel} style={{ marginBottom: '4px' }}>
              {/* Date header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', marginTop: '8px'
              }}>
                <div style={{
                  background: '#F7E9A0', color: '#16325B', borderRadius: '50%',
                  width: '10px', height: '10px', flexShrink: 0,
                }} />
                <span style={{ color: '#F7E9A0', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {dateLabel}
                </span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(108,166,193,0.3)' }} />
              </div>

              {/* Entries for this date */}
              {items.map((entry, idx) => (
                <div key={entry.id} style={{
                  display: 'flex', gap: '12px', marginLeft: '4px',
                  paddingLeft: '16px', borderLeft: idx < items.length - 1 ? '2px solid rgba(108,166,193,0.3)' : '2px solid transparent',
                  paddingBottom: '12px',
                }}>
                  <div style={{ flexShrink: 0, fontSize: '18px', marginTop: '2px' }}>{entry.icon}</div>
                  <div
                    onClick={() => {
                      if (onNavigateTab) {
                        if (entry.type === 'measure') onNavigateTab('measures');
                        else if (entry.type === 'form') onNavigateTab('files');
                        else if (entry.type === 'note') onNavigateTab('treatment');
                      }
                    }}
                    style={{
                    flex: 1, background: '#28527A', borderRadius: '8px', padding: '12px 16px',
                    border: '1px solid rgba(108,166,193,0.2)',
                    cursor: ['measure', 'form', 'note'].includes(entry.type) ? 'pointer' : 'default',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => { if (['measure', 'form', 'note'].includes(entry.type)) e.currentTarget.style.borderColor = '#58A6FF'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(108,166,193,0.2)'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ color: '#f9fafb', fontSize: '14px', fontWeight: 600 }}>
                          {entry.title}
                        </div>
                        {entry.subtitle && (
                          <div style={{ color: '#87CEEB', fontSize: '13px', marginTop: '2px' }}>
                            {entry.subtitle}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {entry.status && (
                          <span style={{
                            background: `${entry.statusColor}20`, color: entry.statusColor,
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                            fontWeight: 500, textTransform: 'capitalize',
                          }}>
                            {entry.status}
                          </span>
                        )}
                        <span style={{ color: '#d1d5db', fontSize: '12px' }}>
                          {entry.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    {entry.details && (
                      <div style={{ color: '#d1d5db', fontSize: '13px', marginTop: '6px', lineHeight: 1.4 }}>
                        {entry.details}
                      </div>
                    )}
                    {entry.meta && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                        {Object.entries(entry.meta).map(([k, v]) => (
                          <span key={k} style={{ color: '#d1d5db', fontSize: '12px' }}>
                            {k}: <span style={{ color: '#d1d5db' }}>{v}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Billing Tab Component
// CPT code mapping for session types
const SESSION_CPT: Record<string, { code: string; label: string; fee: number }> = {
  'initial-consultation': { code: '90791', label: 'Diagnostic Evaluation', fee: 250 },
  'therapy':             { code: '90837', label: 'Psychotherapy 53+ min', fee: 175 },
  'follow-up':           { code: '90834', label: 'Psychotherapy 38-52 min', fee: 150 },
  'assessment':          { code: '96130', label: 'Psych Testing Eval', fee: 200 },
  'couples':             { code: '90847', label: 'Family/Couples Therapy', fee: 200 },
  'other':               { code: '90837', label: 'Psychotherapy 53+ min', fee: 175 },
};

const BillingTab: React.FC<{ client: Patient }> = ({ client }) => {
  const { user } = useAuth();
  const [billingItems, setBillingItems] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(ninetyDaysAgo);
  const [endDate, setEndDate] = useState(todayStr);

  useEffect(() => {
    const loadBilling = async () => {
      const providerId = user?.uid;
      if (!providerId || !client.id) { setLoading(false); return; }
      try {
        const allAppts = await firebaseAppointmentService.getAppointments({ providerId });
        const appts = allAppts.filter(a => a.patientId === client.id || a.patientName === `${client.firstName} ${client.lastName}`);
        // Show completed + in-progress appointments as billing items
        const billable = appts.filter(a => a.status === 'completed' || a.status === 'in-progress');
        setBillingItems(billable.sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime()));
      } catch (err) {
        console.error('Error loading billing data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadBilling();
  }, [client.id, user?.uid]);

  // Filter by date range
  const filteredItems = billingItems.filter(a => {
    const d = a.appointmentDate instanceof Date ? a.appointmentDate : new Date(a.appointmentDate);
    const ds = d.toISOString().split('T')[0];
    return ds >= startDate && ds <= endDate;
  });

  const totalFees = filteredItems.reduce((sum, a) => sum + (SESSION_CPT[a.sessionType]?.fee || 175), 0);

  return (
    <div>
      {/* Coverage verification sits above the billing items: whether the payer
          is active changes how these charges should be handled. */}
      <PatientEligibilityPanel client={client} />
      <div style={{ background: '#28527A', borderRadius: '8px', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ background: '#16325B', border: '1px solid #6CA6C1', borderRadius: '4px', padding: '8px', color: '#f9fafb', colorScheme: 'dark' }} />
            <span style={{ color: '#d1d5db' }}>–</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ background: '#16325B', border: '1px solid #6CA6C1', borderRadius: '4px', padding: '8px', color: '#f9fafb', colorScheme: 'dark' }} />
          </div>
          <div style={{ color: '#f9fafb', fontWeight: 600, fontSize: '15px' }}>
            Total: ${totalFees.toFixed(2)}
          </div>
        </div>

        {/* Billing table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #6CA6C1' }}>
              <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>CPT</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Details</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Duration</th>
              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Fee</th>
              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Insurance</th>
              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Client Owes</th>
              <th style={{ textAlign: 'center', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '20px', color: '#d1d5db', textAlign: 'center' }}>Loading billing data...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '20px', color: '#d1d5db', textAlign: 'center' }}>
                No billing entries for {client.firstName} {client.lastName} in this date range.
              </td></tr>
            ) : (
              filteredItems.map(appt => {
                const cpt = SESSION_CPT[appt.sessionType] || SESSION_CPT['other'];
                const dateStr = (appt.appointmentDate instanceof Date ? appt.appointmentDate : new Date(appt.appointmentDate))
                  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const hasInsurance = !!client.insurance?.provider;
                const insurancePortion = hasInsurance ? cpt.fee * 0.8 : 0;
                const clientPortion = cpt.fee - insurancePortion;

                return (
                  <tr key={appt.id} style={{ borderBottom: '1px solid rgba(108,166,193,0.2)' }}>
                    <td style={{ padding: '10px 8px', color: '#f9fafb', fontSize: '13px' }}>{dateStr}</td>
                    <td style={{ padding: '10px 8px', color: '#58A6FF', fontSize: '13px', fontFamily: 'monospace' }}>{cpt.code}</td>
                    <td style={{ padding: '10px 8px', color: '#f9fafb', fontSize: '13px' }}>{cpt.label}</td>
                    <td style={{ padding: '10px 8px', color: '#d1d5db', fontSize: '13px' }}>{appt.duration} min</td>
                    <td style={{ padding: '10px 8px', color: '#f9fafb', fontSize: '13px', textAlign: 'right' }}>${cpt.fee.toFixed(2)}</td>
                    <td style={{ padding: '10px 8px', color: hasInsurance ? '#10B981' : '#9ca3af', fontSize: '13px', textAlign: 'right' }}>
                      {hasInsurance ? `-$${insurancePortion.toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '10px 8px', color: '#F7E9A0', fontSize: '13px', textAlign: 'right', fontWeight: 600 }}>
                      ${clientPortion.toFixed(2)}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      <span style={{
                        background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b',
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px'
                      }}>Uninvoiced</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Summary row */}
        {filteredItems.length > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '12px 8px',
            borderTop: '2px solid #6CA6C1', marginTop: '8px'
          }}>
            <span style={{ color: '#f9fafb', fontWeight: 600, fontSize: '14px' }}>
              {filteredItems.length} session{filteredItems.length > 1 ? 's' : ''}
            </span>
            <span style={{ color: '#f9fafb', fontWeight: 600, fontSize: '14px' }}>
              Total: ${totalFees.toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Measures Tab — score history graph, scoring guide, in-session administration
interface MeasureScoreEntry { score: number; date: Date; dateLabel: string; responses?: { questionId: string; questionText: string; answer: string }[] }
interface MeasureConfig {
  id: string; title: string; maxScore: number;
  bands: { min: number; max: number; label: string; color: string }[];
  citation?: string;
  questions: { id: string; text: string }[];
}

const ANSWER_OPTIONS = [
  { value: '0', label: 'Not at all' },
  { value: '1', label: 'Several days' },
  { value: '2', label: 'Over half the days' },
  { value: '3', label: 'Nearly every day' },
];

const DIFFICULTY_OPTIONS = [
  { value: '0', label: 'Not difficult at all' },
  { value: '1', label: 'Somewhat difficult' },
  { value: '2', label: 'Very difficult' },
  { value: '3', label: 'Extremely difficult' },
];

// Response value → label mapping for GAD-7/PHQ-9 (0-3 scale)
const RESPONSE_LABELS: Record<string, string> = {
  '0': 'Not at all',
  '1': 'Several days',
  '2': 'Over half the days',
  '3': 'Nearly every day',
};

const MEASURE_CONFIGS: MeasureConfig[] = [
  {
    id: 'gad7', title: 'GAD-7 (Generalized Anxiety)', maxScore: 21,
    bands: [
      { min: 0, max: 4, label: 'None-minimal', color: '#38BDF8' },
      { min: 5, max: 9, label: 'Mild', color: '#10B981' },
      { min: 10, max: 14, label: 'Moderate', color: '#f59e0b' },
      { min: 15, max: 21, label: 'Severe', color: '#EF4444' },
    ],
    citation: 'Spitzer, R. L., Kroenke, K., Williams, J. B., & Löwe, B. (2006). A brief measure for assessing generalized anxiety disorder: the GAD-7. Archives of internal medicine, 166(10), 1092–1097.',
    questions: [
      { id: 'question1', text: 'Feeling nervous, anxious, or on edge.' },
      { id: 'question2', text: 'Not being able to stop or control worrying.' },
      { id: 'question3', text: 'Worrying too much about different things.' },
      { id: 'question4', text: 'Trouble relaxing.' },
      { id: 'question5', text: "Being so restless that it's hard to sit still." },
      { id: 'question6', text: 'Becoming easily annoyed or irritable.' },
      { id: 'question7', text: 'Feeling afraid as if something awful might happen.' },
    ],
  },
  {
    id: 'phq9', title: 'PHQ-9 (Depression)', maxScore: 27,
    bands: [
      { min: 0, max: 4, label: 'None-minimal', color: '#38BDF8' },
      { min: 5, max: 9, label: 'Mild', color: '#10B981' },
      { min: 10, max: 14, label: 'Moderate', color: '#f59e0b' },
      { min: 15, max: 19, label: 'Moderately Severe', color: '#f97316' },
      { min: 20, max: 27, label: 'Severe', color: '#EF4444' },
    ],
    citation: 'Kroenke, K., Spitzer, R. L., & Williams, J. B. (2001). The PHQ-9: validity of a brief depression severity measure. Journal of general internal medicine, 16(9), 606–613.',
    questions: [
      { id: 'question1', text: 'Little interest or pleasure in doing things.' },
      { id: 'question2', text: 'Feeling down, depressed, or hopeless.' },
      { id: 'question3', text: 'Trouble falling or staying asleep, or sleeping too much.' },
      { id: 'question4', text: 'Feeling tired or having little energy.' },
      { id: 'question5', text: 'Poor appetite or overeating.' },
      { id: 'question6', text: 'Feeling bad about yourself — or that you are a failure or have let yourself or your family down.' },
      { id: 'question7', text: 'Trouble concentrating on things, such as reading the newspaper or watching television.' },
      { id: 'question8', text: 'Moving or speaking so slowly that other people could have noticed. Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual.' },
      { id: 'question9', text: 'Thoughts that you would be better off dead or of hurting yourself in some way.' },
    ],
  },
];

const MeasuresTab: React.FC<{ client: Patient }> = ({ client }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [scoresByMeasure, setScoresByMeasure] = useState<Record<string, MeasureScoreEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [showScoringGuide, setShowScoringGuide] = useState<string | null>(null);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [historyCollapsed, setHistoryCollapsed] = useState<Record<string, boolean>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  // Custom measures from Firestore
  const [customMeasures, setCustomMeasures] = useState<CustomMeasure[]>([]);
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = FirebaseCustomMeasureService.subscribeToPracticeMeasures(user.uid, setCustomMeasures);
    return unsub;
  }, [user?.uid]);

  // Merge built-in + custom measures into a single config array
  const allMeasureConfigs: MeasureConfig[] = [
    ...MEASURE_CONFIGS,
    ...customMeasures.map(cm => {
      // Recalculate maxScore from actual scale values (in case saved value is stale)
      const actualScaleMax = Math.max(...(cm.scaleLabels || []).map(sl => parseInt(sl.value, 10) || 0));
      const computedMax = cm.questions.length * actualScaleMax;
      return {
        id: cm.id,
        title: cm.title,
        maxScore: computedMax > 0 ? computedMax : cm.maxScore,
        bands: cm.bands,
        citation: cm.citation,
        questions: cm.questions,
      };
    }),
  ];

  // Custom answer options per measure (built-in use ANSWER_OPTIONS, custom use their own)
  const getAnswerOptions = (measureId: string) => {
    const custom = customMeasures.find(cm => cm.id === measureId);
    if (custom) return custom.scaleLabels.map(sl => ({ value: sl.value, label: sl.label }));
    return ANSWER_OPTIONS;
  };

  // In-session administration state
  const [administeringMeasure, setAdministeringMeasure] = useState<string | null>(null);
  const [adminAnswers, setAdminAnswers] = useState<Record<string, string>>({});
  const [adminDifficulty, setAdminDifficulty] = useState<string>('');
  const [submittingMeasure, setSubmittingMeasure] = useState(false);

  useEffect(() => {
    const loadScores = async () => {
      // Use client's assigned provider so admin staff can see measures too
      const providerId = (client as any).assignedProviderId || user?.uid;
      if (!providerId) { setLoading(false); return; }
      try {
        // Fetch provider-scoped responses AND intake responses (which may have empty providerId)
        const [providerResponses, intakeResponses] = await Promise.all([
          FirebaseFormResponseService.getResponsesByClient(providerId, client.id),
          FirebaseFormResponseService.getResponsesByClient('', client.id).catch(() => [] as any[]),
        ]);

        // Merge and deduplicate by ID
        const seen = new Set<string>();
        const allResponses = [...providerResponses, ...intakeResponses].filter(r => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });

        const byMeasure: Record<string, MeasureScoreEntry[]> = {};

        // Build a set of all known measure IDs (built-in + custom)
        const knownIds = new Set(['gad7', 'phq9', ...customMeasures.map(cm => cm.id)]);

        for (const resp of allResponses) {
          let measureId = '';
          if (resp.formTemplateId === 'gad7' || resp.formTemplateName?.includes('GAD')) measureId = 'gad7';
          else if (resp.formTemplateId === 'phq9' || resp.formTemplateName?.includes('PHQ')) measureId = 'phq9';
          else if (knownIds.has(resp.formTemplateId)) measureId = resp.formTemplateId;
          if (!measureId) continue;

          let total = 0;
          for (const r of (resp.responses || [])) {
            if (r.questionId === 'difficultyLevel') continue;
            const val = parseInt(r.answer, 10);
            if (!isNaN(val)) total += val;
          }

          const d = resp.submittedAt instanceof Date ? resp.submittedAt : new Date(resp.submittedAt || Date.now());
          if (!byMeasure[measureId]) byMeasure[measureId] = [];
          byMeasure[measureId].push({
            score: total,
            date: d,
            dateLabel: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            responses: resp.responses?.map((r: any) => ({ questionId: r.questionId, questionText: r.questionText || r.questionId, answer: r.answer })),
          });
        }

        // Sort each measure's scores by date ascending
        for (const key of Object.keys(byMeasure)) {
          byMeasure[key].sort((a, b) => a.date.getTime() - b.date.getTime());
        }
        setScoresByMeasure(byMeasure);
      } catch (error) {
        console.error('Error loading measure scores:', error);
      } finally {
        setLoading(false);
      }
    };
    loadScores();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, user?.uid, refreshKey, customMeasures]);

  const startAdminister = (measureId: string) => {
    setAdministeringMeasure(measureId);
    setAdminAnswers({});
    setAdminDifficulty('');
  };

  const handleSubmitMeasure = async () => {
    if (!administeringMeasure || !user?.uid) return;
    const config = allMeasureConfigs.find(c => c.id === administeringMeasure);
    if (!config) return;

    // Check all questions answered
    const unanswered = config.questions.filter(q => !adminAnswers[q.id]);
    if (unanswered.length > 0) {
      toast.error('Incomplete', `Please answer all ${config.questions.length} questions.`);
      return;
    }

    setSubmittingMeasure(true);
    try {
      const responses = config.questions.map(q => ({
        questionId: q.id,
        questionText: q.text,
        questionType: 'scale',
        answer: adminAnswers[q.id],
      }));

      if (adminDifficulty) {
        responses.push({
          questionId: 'difficultyLevel',
          questionText: 'How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
          questionType: 'scale',
          answer: adminDifficulty,
        });
      }

      await FirebaseFormResponseService.saveResponse({
        assignmentId: `session_${administeringMeasure}_${Date.now()}`,
        clientId: client.id,
        providerId: user.uid,
        formTemplateId: administeringMeasure,
        formTemplateName: config.title,
        responses,
        submittedBy: user.uid,
      });

      // Also mark as completed in form_assignments so it shows in Forms tab
      await FirebaseFormAssignmentService.markMeasureCompleted({
        clientId: client.id,
        providerId: user.uid,
        formTemplateId: administeringMeasure,
        formTemplateName: config.title,
      });

      // Calculate the score for the toast
      let total = 0;
      for (const q of config.questions) {
        const val = parseInt(adminAnswers[q.id], 10);
        if (!isNaN(val)) total += val;
      }
      const sev = getSeverity(config, total);

      toast.success('Measure Recorded',
        `${config.title} score: ${total}/${config.maxScore} (${sev.label})`);
      setAdministeringMeasure(null);
      setAdminAnswers({});
      setAdminDifficulty('');
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      toast.error('Save Failed', error?.message || 'Could not save measure.');
    } finally {
      setSubmittingMeasure(false);
    }
  };

  // Live running total while administering
  const getRunningTotal = () => {
    if (!administeringMeasure) return 0;
    let total = 0;
    for (const val of Object.values(adminAnswers)) {
      const n = parseInt(val, 10);
      if (!isNaN(n)) total += n;
    }
    return total;
  };

  const getSeverity = (config: MeasureConfig, score: number) => {
    for (const band of config.bands) {
      if (score >= band.min && score <= band.max) return band;
    }
    return config.bands[config.bands.length - 1];
  };

  // SVG Line Chart for score history
  const renderGraph = (config: MeasureConfig, scores: MeasureScoreEntry[]) => {
    const W = 500, H = 180, PAD = { top: 20, right: 80, bottom: 30, left: 40 };
    const chartW = W - PAD.left - PAD.right;
    const chartH = H - PAD.top - PAD.bottom;

    // Y axis: 0 to effective max (use higher of config.maxScore or actual highest score)
    const effectiveMax = Math.max(1, config.maxScore, ...scores.map(s => s.score));
    const yScale = (val: number) => PAD.top + chartH - (val / effectiveMax) * chartH;
    // X axis: spread points evenly if 1 point, else by date range
    const minT = scores.length > 1 ? scores[0].date.getTime() : 0;
    const maxT = scores.length > 1 ? scores[scores.length - 1].date.getTime() : 1;
    const xScale = (d: Date) => {
      if (scores.length <= 1) return PAD.left + chartW / 2;
      const range = maxT - minT || 1;
      return PAD.left + ((d.getTime() - minT) / range) * chartW;
    };

    // Build severity band background rects
    const bandRects = config.bands.map((band, i) => {
      const y1 = yScale(band.max);
      const y2 = yScale(band.min);
      return (
        <rect key={i} x={PAD.left} y={y1} width={chartW} height={y2 - y1}
          fill={band.color} opacity={0.08} />
      );
    });

    // Line path
    const points = scores.map(s => ({ x: xScale(s.date), y: yScale(s.score) }));
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
        {/* Band backgrounds */}
        {bandRects}

        {/* Y-axis grid lines */}
        {[0, Math.round(effectiveMax * 0.25), Math.round(effectiveMax * 0.5), Math.round(effectiveMax * 0.75), effectiveMax].map(v => (
          <g key={v}>
            <line x1={PAD.left} y1={yScale(v)} x2={PAD.left + chartW} y2={yScale(v)}
              stroke="rgba(108,166,193,0.2)" strokeDasharray="3,3" />
            <text x={PAD.left - 8} y={yScale(v) + 4} textAnchor="end"
              fill="#6b7280" fontSize="11">{v}</text>
          </g>
        ))}

        {/* Line */}
        {points.length > 1 && (
          <path d={linePath} fill="none" stroke="#58A6FF" strokeWidth="2.5" strokeLinejoin="round" />
        )}

        {/* Data points */}
        {points.map((p, i) => {
          const sev = getSeverity(config, scores[i].score);
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="6" fill={sev.color} stroke="#16325B" strokeWidth="2" />
              <text x={p.x} y={p.y - 10} textAnchor="middle" fill="#f9fafb" fontSize="11" fontWeight="600">
                {scores[i].score}
              </text>
            </g>
          );
        })}

        {/* X-axis date labels */}
        {scores.map((s, i) => (
          <text key={i} x={xScale(s.date)} y={H - 4} textAnchor="middle" fill="#6b7280" fontSize="10">
            {s.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </text>
        ))}

        {/* Severity band labels on right */}
        {config.bands.map((band, i) => {
          const midY = (yScale(band.max) + yScale(band.min)) / 2;
          return (
            <g key={i}>
              <rect x={PAD.left + chartW + 8} y={midY - 8} width="4" height="16" rx="2" fill={band.color} />
              <text x={PAD.left + chartW + 16} y={midY + 4} fill={band.color} fontSize="10" fontWeight="500">
                {band.label}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  const renderMeasureCard = (config: MeasureConfig) => {
    const scores = scoresByMeasure[config.id] || [];
    const latest = scores.length > 0 ? scores[scores.length - 1] : null;
    const baseline = scores.length > 0 ? scores[0] : null;
    const previous = scores.length > 1 ? scores[scores.length - 2] : null;

    const latestSeverity = latest ? getSeverity(config, latest.score) : null;
    const changeSinceBaseline = latest && baseline && scores.length > 1 ? latest.score - baseline.score : null;
    const changeSinceLast = latest && previous ? latest.score - previous.score : null;

    return (
      <div key={config.id} style={{
        background: '#28527A', borderRadius: '8px', padding: '20px', marginBottom: '20px',
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>{config.title}</h3>
            {scores.length > 0 && (
              <div style={{ display: 'flex', gap: '16px', marginTop: '6px' }}>
                {changeSinceBaseline !== null && (
                  <span style={{ color: changeSinceBaseline <= 0 ? '#10B981' : '#EF4444', fontSize: '13px', fontWeight: 500 }}>
                    {changeSinceBaseline <= 0 ? '↓' : '↑'} {Math.abs(changeSinceBaseline)} since baseline
                  </span>
                )}
                {changeSinceLast !== null && (
                  <span style={{ color: changeSinceLast <= 0 ? '#10B981' : '#EF4444', fontSize: '13px', fontWeight: 500 }}>
                    {changeSinceLast <= 0 ? '↓' : '↑'} {Math.abs(changeSinceLast)} since last
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowScoringGuide(showScoringGuide === config.id ? null : config.id)}
            style={{
              background: 'none', border: '1px solid #6CA6C1', borderRadius: '4px',
              padding: '4px 10px', color: '#58A6FF', cursor: 'pointer', fontSize: '12px'
            }}
          >
            Scoring Guide
          </button>
        </div>

        {/* Scoring guide modal */}
        {showScoringGuide === config.id && (
          <div style={{
            background: '#16325B', borderRadius: '8px', padding: '16px', marginBottom: '16px',
            border: '1px solid #6CA6C1'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '15px' }}>{config.title.split('(')[0].trim()} Scoring Guide</h4>
              <button onClick={() => setShowScoringGuide(null)}
                style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '18px' }}>x</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #6CA6C1' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#d1d5db', fontSize: '12px' }}>Score</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#d1d5db', fontSize: '12px' }}>Severity</th>
                </tr>
              </thead>
              <tbody>
                {config.bands.map((band, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(108,166,193,0.2)' }}>
                    <td style={{ padding: '10px 8px', color: '#f9fafb', fontSize: '14px' }}>
                      <span style={{
                        display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%',
                        background: band.color, marginRight: '8px'
                      }} />
                      {band.min}–{band.max}
                    </td>
                    <td style={{ padding: '10px 8px', color: '#f9fafb', fontSize: '14px' }}>{band.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {config.citation && (
              <p style={{ color: '#d1d5db', fontSize: '11px', marginTop: '12px', lineHeight: 1.4, fontStyle: 'italic' }}>
                {config.citation}
              </p>
            )}
          </div>
        )}

        {scores.length === 0 ? (
          <div style={{ color: '#d1d5db', fontSize: '14px', padding: '20px 0', textAlign: 'center' }}>
            No {config.title.split('(')[0].trim()} assessments completed yet.
          </div>
        ) : (
          <>
            {/* Score history graph */}
            <div style={{ background: '#16325B', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>All Time</span>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>{scores.length} assessment{scores.length > 1 ? 's' : ''}</span>
              </div>
              {renderGraph(config, scores)}
            </div>

            {/* Latest score summary */}
            {latest && latestSeverity && (
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
                {/* Semi-circle gauge */}
                <div style={{ position: 'relative', width: '80px', height: '48px', overflow: 'hidden' }}>
                  <svg viewBox="0 0 80 48" style={{ width: '80px', height: '48px' }}>
                    {/* Background arc */}
                    <path d="M 8 44 A 32 32 0 0 1 72 44" fill="none" stroke="#16325B" strokeWidth="6" strokeLinecap="round" />
                    {/* Score arc */}
                    <path d="M 8 44 A 32 32 0 0 1 72 44" fill="none" stroke={latestSeverity.color} strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={`${(latest.score / config.maxScore) * 100} 200`} />
                  </svg>
                  <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f9fafb', lineHeight: 1 }}>{latest.score}</div>
                  </div>
                </div>
                <div>
                  <div style={{ marginBottom: '4px' }}>
                    <span style={{
                      background: latestSeverity.color, color: 'white', padding: '3px 10px',
                      borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                    }}>
                      {latestSeverity.label}
                    </span>
                  </div>
                  <div style={{ color: '#d1d5db', fontSize: '12px' }}>
                    Latest: {latest.dateLabel} · {latest.score}/{config.maxScore}
                  </div>
                  {/* Scoring interpretation */}
                  <div style={{ color: '#d1d5db', fontSize: '12px', marginTop: '6px', lineHeight: 1.5 }}>
                    {config.id === 'gad7' && latestSeverity && (
                      <>Scores from {latestSeverity.min}–{latestSeverity.max} indicate <strong style={{ color: '#f9fafb' }}>{latestSeverity.label.toLowerCase()} anxiety</strong>.
                      {latest.score >= 10 ? ' Consider further evaluation and possible treatment.' : latest.score >= 5 ? ' Monitor symptoms and reassess.' : ' No clinical action typically needed.'}</>
                    )}
                    {config.id === 'phq9' && latestSeverity && (
                      <>Scores from {latestSeverity.min}–{latestSeverity.max} indicate <strong style={{ color: '#f9fafb' }}>{latestSeverity.label.toLowerCase()} depression</strong>.
                      {latest.score >= 15 ? ' Active treatment strongly recommended.' : latest.score >= 10 ? ' Consider counseling and/or pharmacotherapy.' : latest.score >= 5 ? ' Watchful waiting; repeat at follow-up.' : ' No clinical action typically needed.'}</>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Score history list with expandable responses */}
            <div style={{ borderTop: '1px solid rgba(108,166,193,0.3)', paddingTop: '12px' }}>
              <div
                onClick={() => setHistoryCollapsed(prev => ({ ...prev, [config.id]: !prev[config.id] }))}
                style={{ color: '#d1d5db', fontSize: '12px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>Assessment History ({scores.length})</span>
                <span style={{ fontSize: '11px' }}>{historyCollapsed[config.id] ? '▶' : '▼'}</span>
              </div>
              {!historyCollapsed[config.id] && (() => {
                // Group scores by date
                const reversed = [...scores].reverse();
                const dateGroups: Record<string, typeof reversed> = {};
                reversed.forEach(s => {
                  const key = s.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
                  if (!dateGroups[key]) dateGroups[key] = [];
                  dateGroups[key].push(s);
                });
                return Object.entries(dateGroups).map(([dateKey, entries]) => (
                  <div key={dateKey} style={{ marginBottom: '8px' }}>
                    {/* Date header — timeline style */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '10px 0 6px' }}>
                      <div style={{ background: '#F7E9A0', borderRadius: '50%', width: '8px', height: '8px', flexShrink: 0 }} />
                      <span style={{ color: '#F7E9A0', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' }}>{dateKey}</span>
                      <div style={{ flex: 1, height: '1px', background: 'rgba(108,166,193,0.25)' }} />
                    </div>
                    {entries.map((s, i) => {
                      const sev = getSeverity(config, s.score);
                      const globalIdx = reversed.indexOf(s);
                      const entryKey = `${config.id}-${globalIdx}`;
                      const isExpanded = expandedEntry === entryKey;
                      return (
                        <div key={i} style={{ marginLeft: '18px' }}>
                          <div
                            onClick={() => setExpandedEntry(isExpanded ? null : entryKey)}
                            style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid rgba(108,166,193,0.15)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: sev.color
                              }} />
                              <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: 600 }}>{s.score}</span>
                              <span style={{ color: '#d1d5db', fontSize: '13px' }}>— {sev.label}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: '#d1d5db', fontSize: '12px' }}>
                                {s.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span style={{ color: '#d1d5db', fontSize: '12px' }}>{isExpanded ? '▼' : '▶'}</span>
                            </div>
                          </div>
                          {isExpanded && s.responses && (
                            <div style={{ background: '#16325B', borderRadius: '6px', padding: '12px', margin: '8px 0 12px' }}>
                              {s.responses.filter(r => r.questionId !== 'difficultyLevel').map((r, qi) => (
                                <div key={qi} style={{
                                  display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                                  borderBottom: qi < s.responses!.length - 2 ? '1px solid rgba(108,166,193,0.1)' : 'none'
                                }}>
                                  <span style={{ color: '#e5e7eb', fontSize: '13px', flex: 1, paddingRight: '12px' }}>
                                    {qi + 1}. {r.questionText}
                                  </span>
                                  <span style={{ color: '#58A6FF', fontSize: '13px', fontWeight: 500, flexShrink: 0 }}>
                                    {r.answer}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <div style={{ color: '#d1d5db', padding: '24px', textAlign: 'center' }}>Loading measures...</div>;
  }

  return (
    <div>
      {/* Administer measure buttons */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap',
        alignItems: 'center', justifyContent: 'space-between'
      }}>
        <span style={{ color: '#d1d5db', fontSize: '13px' }}>Administer during session:</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          {allMeasureConfigs.map(config => (
            <button key={config.id}
              onClick={() => administeringMeasure === config.id ? setAdministeringMeasure(null) : startAdminister(config.id)}
              disabled={submittingMeasure}
              style={{
                background: administeringMeasure === config.id ? '#1d4ed8' : '#16325B',
                border: `1px solid ${administeringMeasure === config.id ? '#3b82f6' : '#6CA6C1'}`,
                borderRadius: '6px',
                padding: '6px 14px', color: '#58A6FF', cursor: submittingMeasure ? 'not-allowed' : 'pointer',
                fontSize: '13px', fontWeight: 500,
              }}>
              {administeringMeasure === config.id ? '✕ Cancel' : `+ ${config.title}`}
            </button>
          ))}
        </div>
      </div>

      {/* In-session questionnaire form */}
      {administeringMeasure && (() => {
        const config = allMeasureConfigs.find(c => c.id === administeringMeasure);
        if (!config) return null;
        const answeredCount = config.questions.filter(q => adminAnswers[q.id] !== undefined).length;
        const runningTotal = getRunningTotal();
        const currentSeverity = getSeverity(config, runningTotal);

        return (
          <div style={{
            background: '#16325B', borderRadius: '10px', padding: '24px', marginBottom: '20px',
            border: '2px solid #3b82f6',
          }}>
            {/* Form header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>
                {config.title}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#d1d5db', fontSize: '13px' }}>
                  {answeredCount}/{config.questions.length} answered
                </span>
                {answeredCount > 0 && (
                  <span style={{
                    background: currentSeverity.color, color: 'white', padding: '3px 10px',
                    borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                  }}>
                    Score: {runningTotal} — {currentSeverity.label}
                  </span>
                )}
              </div>
            </div>
            <p style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '20px' }}>
              Over the last 2 weeks, how often have you been bothered by the following problems?
            </p>

            {/* Questions */}
            {config.questions.map((q, qi) => (
              <div key={q.id} style={{
                marginBottom: '16px', padding: '14px', borderRadius: '8px',
                background: adminAnswers[q.id] !== undefined ? 'rgba(59,130,246,0.08)' : '#0f1f35',
                border: `1px solid ${adminAnswers[q.id] !== undefined ? 'rgba(59,130,246,0.3)' : 'rgba(108,166,193,0.2)'}`,
              }}>
                <div style={{ color: '#e5e7eb', fontSize: '14px', marginBottom: '10px' }}>
                  <span style={{ color: '#58A6FF', fontWeight: 600 }}>{qi + 1}.</span> {q.text}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {getAnswerOptions(administeringMeasure!).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setAdminAnswers(prev => ({ ...prev, [q.id]: opt.value }))}
                      style={{
                        background: adminAnswers[q.id] === opt.value ? '#2563eb' : '#28527A',
                        border: `1px solid ${adminAnswers[q.id] === opt.value ? '#3b82f6' : '#6CA6C1'}`,
                        borderRadius: '6px', padding: '6px 12px',
                        color: adminAnswers[q.id] === opt.value ? 'white' : '#e5e7eb',
                        cursor: 'pointer', fontSize: '13px',
                        fontWeight: adminAnswers[q.id] === opt.value ? 600 : 400,
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ fontWeight: 600, marginRight: '4px' }}>{opt.value}</span> {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Difficulty question */}
            <div style={{
              marginBottom: '20px', padding: '14px', borderRadius: '8px',
              background: '#0f1f35', border: '1px dashed rgba(108,166,193,0.3)',
            }}>
              <div style={{ color: '#d1d5db', fontSize: '13px', marginBottom: '10px' }}>
                If you checked off any problems, how difficult have these made it for you to do your work, take care of things at home, or get along with other people?
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {DIFFICULTY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setAdminDifficulty(opt.value)}
                    style={{
                      background: adminDifficulty === opt.value ? '#7c3aed' : '#28527A',
                      border: `1px solid ${adminDifficulty === opt.value ? '#8b5cf6' : '#6CA6C1'}`,
                      borderRadius: '6px', padding: '6px 12px',
                      color: adminDifficulty === opt.value ? 'white' : '#e5e7eb',
                      cursor: 'pointer', fontSize: '13px',
                      fontWeight: adminDifficulty === opt.value ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center' }}>
              {answeredCount > 0 && (
                <span style={{ color: '#d1d5db', fontSize: '13px', marginRight: 'auto' }}>
                  Running total: <span style={{ color: currentSeverity.color, fontWeight: 600 }}>{runningTotal}/{config.maxScore}</span>
                </span>
              )}
              <button
                onClick={() => { setAdministeringMeasure(null); setAdminAnswers({}); setAdminDifficulty(''); }}
                style={{
                  background: '#374151', border: 'none', borderRadius: '6px',
                  padding: '10px 20px', color: '#e5e7eb', cursor: 'pointer', fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitMeasure}
                disabled={answeredCount < config.questions.length || submittingMeasure}
                style={{
                  background: answeredCount === config.questions.length && !submittingMeasure ? '#10b981' : '#6b7280',
                  border: 'none', borderRadius: '6px', padding: '10px 24px',
                  color: 'white', cursor: answeredCount === config.questions.length && !submittingMeasure ? 'pointer' : 'not-allowed',
                  fontSize: '14px', fontWeight: 600,
                }}
              >
                {submittingMeasure ? 'Saving...' : 'Save & Score'}
              </button>
            </div>
          </div>
        );
      })()}

      {allMeasureConfigs.map(config => renderMeasureCard(config))}
    </div>
  );
};

// Files Tab Component
interface UploadedFile {
  id: string;
  fileName: string;
  documentTitle?: string;
  category: string;
  downloadUrl: string;
  uploadedAt: Date;
  uploadedBy: string;
  fileSize: number;
  contentType: string;
  documentDate?: string;
}

// Chart Order tab categories for organizing client files (matches clinical chart order)
const CHART_ORDER_TABS = [
  { id: 'legals', label: 'Legals', description: 'Consent forms, HIPAA notices, releases of information, intake paperwork' },
  { id: 'assessments', label: 'Assessments', description: 'BHA, PHQ-9, GAD-7, clinical assessments, evaluations' },
  { id: 'treatment-plans', label: 'Treatment Plans', description: 'Treatment plans, reviews, updates, goal tracking' },
  { id: 'progress-notes', label: 'Progress Notes', description: 'SOAP, DAP, BIRP notes, session documentation' },
  { id: 'correspondence', label: 'Correspondence', description: 'Letters, referrals, collateral contacts, communication' },
  { id: 'insurance-billing', label: 'Insurance / Billing', description: 'Insurance cards, claims, EOBs, superbills, payment records' },
  { id: 'miscellaneous', label: 'Miscellaneous', description: 'Lab results, external records, medication lists, other documents' },
  { id: 'discharge', label: 'Discharge', description: 'Discharge summaries, transfer records, closing documentation' },
] as const;

type ChartOrderTabId = typeof CHART_ORDER_TABS[number]['id'];

const FilesTab: React.FC<{ client: Patient }> = ({ client }) => {
  const toast = useToast();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<Array<{
    id: string;
    title: string;
    category: string;
    createdAt: string;
    pdfUrl?: string;
    formName: string;
    submittedDate: string;
    responses: Array<{ questionId: string; questionText: string; questionType: string; answer: any }>;
    formTemplateId: string;
  }>>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<typeof documents[0] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<ChartOrderTabId>('legals');
  const [uploadDocumentDate, setUploadDocumentDate] = useState('');
  const [uploadDocumentTitle, setUploadDocumentTitle] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [activeSection, setActiveSection] = useState<'forms' | 'chart-order'>('chart-order');
  const [activeChartTab, setActiveChartTab] = useState<ChartOrderTabId>('legals');

  useEffect(() => {
    const loadDocuments = async () => {
      const providerId = user?.uid;
      if (!providerId) return;

      try {
        // Load form responses
        const responses = await FirebaseFormResponseService.getResponsesByClient(providerId, client.id);
        const docs = responses.map(resp => ({
          id: resp.id,
          title: resp.formTemplateName || 'Form Response',
          category: resp.formTemplateName?.includes('PHQ') ? 'Assessment' :
                   resp.formTemplateName?.includes('GAD') ? 'Assessment' :
                   resp.formTemplateName?.includes('Intake') ? 'Intake' : 'Clinical',
          createdAt: resp.submittedAt instanceof Date ? resp.submittedAt.toISOString() : new Date().toISOString(),
          pdfUrl: undefined,
          formName: resp.formTemplateName || 'Unknown Form',
          submittedDate: resp.submittedAt instanceof Date
            ? resp.submittedAt.toLocaleDateString()
            : new Date().toLocaleDateString(),
          responses: resp.responses || [],
          formTemplateId: resp.formTemplateId || '',
        }));
        setDocuments(docs);

        // Load uploaded files from Firestore
        const filesQuery = query(
          collection(db, 'client_files'),
          where('clientId', '==', client.id),
          where('providerId', '==', providerId),
          orderBy('uploadedAt', 'desc')
        );
        const snap = await getDocs(filesQuery);
        const files: UploadedFile[] = snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            fileName: data.fileName,
            documentTitle: data.documentTitle || '',
            category: data.category,
            downloadUrl: data.downloadUrl,
            uploadedAt: data.uploadedAt?.toDate?.() || new Date(),
            uploadedBy: data.uploadedBy,
            fileSize: data.fileSize || 0,
            contentType: data.contentType || '',
            documentDate: data.documentDate || '',
          };
        });
        setUploadedFiles(files);
      } catch (error) {
        console.error('Error loading files:', error);
      }
    };

    loadDocuments();
  }, [client.id, user?.uid]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user?.uid) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const storagePath = `client_files/${user.uid}/${client.id}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);

        const docRef = await addDoc(collection(db, 'client_files'), {
          clientId: client.id,
          providerId: user.uid,
          fileName: file.name,
          documentTitle: uploadDocumentTitle || '',
          category: uploadCategory,
          documentDate: uploadDocumentDate || '',
          downloadUrl,
          storagePath,
          fileSize: file.size,
          contentType: file.type,
          uploadedBy: user.displayName || user.email || user.uid,
          uploadedAt: Timestamp.now(),
        });

        setUploadedFiles(prev => [{
          id: docRef.id,
          fileName: file.name,
          documentTitle: uploadDocumentTitle || '',
          category: uploadCategory,
          downloadUrl,
          uploadedAt: new Date(),
          uploadedBy: user.displayName || 'Provider',
          fileSize: file.size,
          contentType: file.type,
          documentDate: uploadDocumentDate || '',
        }, ...prev]);
      }
      toast.success('Uploaded', `${files.length} file${files.length > 1 ? 's' : ''} uploaded to ${CHART_ORDER_TABS.find(t => t.id === uploadCategory)?.label || uploadCategory}.`);
      setUploadDocumentDate('');
      setUploadDocumentTitle('');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Upload Failed', error?.message || 'Could not upload file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.formName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get files for a specific chart order tab
  const getFilesForTab = (tabId: ChartOrderTabId): UploadedFile[] => {
    return uploadedFiles
      .filter(f => f.category === tabId)
      .filter(f => !searchQuery || f.fileName.toLowerCase().includes(searchQuery.toLowerCase()) || (f.documentTitle && f.documentTitle.toLowerCase().includes(searchQuery.toLowerCase())))
      .sort((a, b) => {
        // Sort by document date (newest first), then upload date
        if (a.documentDate && b.documentDate) return b.documentDate.localeCompare(a.documentDate);
        if (a.documentDate) return -1;
        if (b.documentDate) return 1;
        return b.uploadedAt.getTime() - a.uploadedAt.getTime();
      });
  };

  // Count files per tab
  const tabFileCounts = CHART_ORDER_TABS.reduce((acc, tab) => {
    acc[tab.id] = uploadedFiles.filter(f => f.category === tab.id).length;
    return acc;
  }, {} as Record<ChartOrderTabId, number>);

  // Files that don't match any chart order tab (legacy categories)
  const uncategorizedFiles = uploadedFiles.filter(f =>
    !CHART_ORDER_TABS.some(tab => tab.id === f.category)
  );

  const calculateScore = (responses: typeof documents[0]['responses'], formTemplateId: string): number | null => {
    if (formTemplateId !== 'gad7' && formTemplateId !== 'phq9') return null;
    let total = 0;
    for (const r of responses) {
      if (r.questionId === 'difficultyLevel') continue;
      const val = parseInt(r.answer, 10);
      if (!isNaN(val)) total += val;
    }
    return total;
  };

  const getSeverityLabel = (score: number, formTemplateId: string): { label: string; color: string } => {
    if (formTemplateId === 'gad7') {
      if (score <= 4) return { label: 'Minimal Anxiety', color: '#10B981' };
      if (score <= 9) return { label: 'Mild Anxiety', color: '#f59e0b' };
      if (score <= 14) return { label: 'Moderate Anxiety', color: '#f97316' };
      return { label: 'Severe Anxiety', color: '#ef4444' };
    }
    if (score <= 4) return { label: 'Minimal Depression', color: '#10B981' };
    if (score <= 9) return { label: 'Mild Depression', color: '#f59e0b' };
    if (score <= 14) return { label: 'Moderate Depression', color: '#f97316' };
    if (score <= 19) return { label: 'Moderately Severe Depression', color: '#ef4444' };
    return { label: 'Severe Depression', color: '#dc2626' };
  };

  const scaleLabels: Record<string, string> = {
    '0': 'Not at all',
    '1': 'Several days',
    '2': 'More than half the days',
    '3': 'Nearly every day',
  };

  const currentTabFiles = getFilesForTab(activeChartTab);
  const currentTabInfo = CHART_ORDER_TABS.find(t => t.id === activeChartTab)!;

  return (
    <div>
      {/* Response Detail Modal */}
      {selectedDoc && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
        }} onClick={() => setSelectedDoc(null)}>
          <div style={{
            background: '#1a3a5c', borderRadius: '12px', padding: '24px',
            maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto',
            border: '1px solid #6CA6C1',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ color: '#f9fafb', margin: 0 }}>{selectedDoc.formName}</h3>
                <span style={{ color: '#d1d5db', fontSize: '13px' }}>Submitted: {selectedDoc.submittedDate}</span>
              </div>
              <button onClick={() => setSelectedDoc(null)} style={{
                background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '20px',
              }}>X</button>
            </div>

            {(() => {
              const score = calculateScore(selectedDoc.responses, selectedDoc.formTemplateId);
              if (score === null) return null;
              const severity = getSeverityLabel(score, selectedDoc.formTemplateId);
              const maxScore = selectedDoc.formTemplateId === 'gad7' ? 21 : 27;
              return (
                <div style={{
                  background: '#16325B', borderRadius: '8px', padding: '16px', marginBottom: '16px',
                  display: 'flex', alignItems: 'center', gap: '16px',
                }}>
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '50%',
                    background: severity.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 'bold', fontSize: '22px', color: 'white',
                  }}>{score}</div>
                  <div>
                    <div style={{ color: '#f9fafb', fontWeight: 600, fontSize: '16px' }}>{severity.label}</div>
                    <div style={{ color: '#d1d5db', fontSize: '13px' }}>Score: {score} / {maxScore}</div>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedDoc.responses.map((r, idx) => (
                <div key={r.questionId || idx} style={{
                  background: '#16325B', borderRadius: '8px', padding: '12px 16px',
                }}>
                  <div style={{ color: '#d1d5db', fontSize: '13px', marginBottom: '4px' }}>
                    Question {idx + 1}
                  </div>
                  <div style={{ color: '#f9fafb', fontSize: '14px', marginBottom: '8px' }}>
                    {r.questionText || r.questionId}
                  </div>
                  <div style={{
                    color: '#58A6FF', fontSize: '14px', fontWeight: 500,
                    background: '#0d2137', borderRadius: '4px', padding: '8px 12px',
                  }}>
                    {r.questionType === 'scale'
                      ? `${r.answer} — ${scaleLabels[String(r.answer)] || r.answer}`
                      : Array.isArray(r.answer)
                        ? r.answer.join(', ')
                        : String(r.answer)
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Section Toggle: Chart Order vs Form Responses */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => setActiveSection('chart-order')}
          style={{
            background: activeSection === 'chart-order' ? '#D4A017' : 'transparent',
            border: activeSection === 'chart-order' ? 'none' : '1px solid #6CA6C1',
            borderRadius: '20px', padding: '8px 18px',
            color: activeSection === 'chart-order' ? '#113F67' : '#d1d5db',
            cursor: 'pointer', fontSize: '14px', fontWeight: activeSection === 'chart-order' ? 600 : 400,
          }}>
          Digital Filing Cabinet ({uploadedFiles.length})
        </button>
        <button onClick={() => setActiveSection('forms')}
          style={{
            background: activeSection === 'forms' ? '#58A6FF' : 'transparent',
            border: activeSection === 'forms' ? 'none' : '1px solid #6CA6C1',
            borderRadius: '20px', padding: '8px 18px',
            color: activeSection === 'forms' ? 'white' : '#d1d5db',
            cursor: 'pointer', fontSize: '14px', fontWeight: activeSection === 'forms' ? 600 : 400,
          }}>
          Form Responses ({documents.length})
        </button>
      </div>

      {/* Chart Order View */}
      {activeSection === 'chart-order' && (
        <div style={{ background: '#28527A', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Chart Order Tab Navigation */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '2px',
            background: '#113F67', padding: '8px 8px 0', borderBottom: '1px solid #6CA6C1',
          }}>
            {CHART_ORDER_TABS.map(tab => (
              <button key={tab.id} onClick={() => { setActiveChartTab(tab.id); setUploadCategory(tab.id); }}
                style={{
                  padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
                  borderRadius: '6px 6px 0 0',
                  background: activeChartTab === tab.id ? '#28527A' : 'transparent',
                  color: activeChartTab === tab.id ? '#F7E9A0' : '#9ca3af',
                  borderBottom: activeChartTab === tab.id ? '2px solid #D4A017' : '2px solid transparent',
                  position: 'relative',
                }}>
                {tab.label}
                {tabFileCounts[tab.id] > 0 && (
                  <span style={{
                    marginLeft: '6px', background: activeChartTab === tab.id ? '#D4A017' : '#6b7280',
                    color: activeChartTab === tab.id ? '#113F67' : '#fff',
                    padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: 700,
                  }}>{tabFileCounts[tab.id]}</span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ padding: '20px' }}>
            {/* Tab header with description and upload controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h4 style={{ color: '#F7E9A0', margin: '0 0 4px 0', fontSize: '16px' }}>{currentTabInfo.label}</h4>
                <p style={{ color: '#9ca3af', margin: 0, fontSize: '13px' }}>{currentTabInfo.description}</p>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" placeholder="Search files..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ background: '#16325B', border: '1px solid #6CA6C1', borderRadius: '4px', padding: '6px 10px', color: '#f9fafb', width: '140px', fontSize: '13px' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 500 }}>Document Title</label>
                  <input type="text" placeholder="e.g. BHA Assessment" value={uploadDocumentTitle}
                    onChange={(e) => setUploadDocumentTitle(e.target.value)}
                    style={{ background: '#16325B', border: '1px solid #6CA6C1', borderRadius: '4px', padding: '5px 8px', color: '#f9fafb', fontSize: '12px', width: '150px' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 500 }}>Document Date</label>
                  <input type="date" value={uploadDocumentDate} onChange={(e) => setUploadDocumentDate(e.target.value)}
                    style={{ background: '#16325B', border: '1px solid #6CA6C1', borderRadius: '4px', padding: '5px 8px', color: '#f9fafb', fontSize: '12px' }} />
                </div>
                <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.csv"
                  onChange={handleFileUpload} style={{ display: 'none' }} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  style={{
                    background: uploading ? '#6b7280' : '#10B981', border: 'none', borderRadius: '6px',
                    padding: '8px 16px', color: 'white', cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', alignSelf: 'flex-end',
                  }}>
                  {uploading ? 'Uploading...' : '+ Upload to ' + currentTabInfo.label}
                </button>
              </div>
            </div>

            {/* Drag-and-drop zone + Files list for current tab */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                if (e.dataTransfer.files.length > 0) {
                  const input = fileInputRef.current;
                  if (input) {
                    const dt = new DataTransfer();
                    Array.from(e.dataTransfer.files).forEach(f => dt.items.add(f));
                    input.files = dt.files;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              }}
              style={{
                border: isDragOver ? '2px dashed #10B981' : '2px dashed transparent',
                borderRadius: '8px',
                backgroundColor: isDragOver ? 'rgba(16,185,129,0.05)' : 'transparent',
                transition: 'all 0.2s',
                minHeight: '80px',
              }}
            >
            {currentTabFiles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#d1d5db', cursor: 'pointer' }}
                onClick={() => fileInputRef.current?.click()}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>&#128193;</div>
                <p style={{ fontSize: '15px', color: '#e5e7eb', marginBottom: '4px' }}>No files in {currentTabInfo.label}</p>
                <p style={{ fontSize: '13px' }}>{currentTabInfo.description}</p>
                <p style={{ fontSize: '12px', color: '#6CA6C1', marginTop: '8px' }}>Click or drag files here to upload</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #6CA6C1' }}>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Document</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Size</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Uploaded</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTabFiles.map(file => (
                    <tr key={file.id} style={{ borderBottom: '1px solid rgba(108,166,193,0.2)' }}>
                      <td style={{ padding: '10px 8px', fontSize: '13px' }}>
                        <div style={{ color: '#f9fafb' }}>
                          {file.contentType.includes('pdf') ? '\u{1F4C4}' : file.contentType.includes('image') ? '\u{1F5BC}' : '\u{1F4CE}'} {file.documentTitle || file.fileName}
                        </div>
                        {file.documentTitle && (
                          <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>{file.fileName}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#d1d5db', fontSize: '13px' }}>
                        {file.documentDate ? new Date(file.documentDate + 'T00:00:00').toLocaleDateString('en-US') : '\u2014'}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#d1d5db', fontSize: '13px' }}>{formatFileSize(file.fileSize)}</td>
                      <td style={{ padding: '10px 8px', color: '#d1d5db', fontSize: '13px' }}>
                        {file.uploadedAt.toLocaleDateString()}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <a href={file.downloadUrl} target="_blank" rel="noopener noreferrer"
                          style={{ background: '#58A6FF', border: 'none', borderRadius: '4px', padding: '4px 12px', color: 'white', cursor: 'pointer', fontSize: '12px', textDecoration: 'none', display: 'inline-block' }}>
                          View / Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>{/* end drag-and-drop zone */}

            {/* Uncategorized files notice */}
            {uncategorizedFiles.length > 0 && activeChartTab === 'legals' && (
              <div style={{
                marginTop: '16px', padding: '12px 16px', background: '#113F67',
                borderRadius: '6px', border: '1px solid #f59e0b',
              }}>
                <p style={{ color: '#f59e0b', fontSize: '13px', margin: '0 0 4px 0', fontWeight: 600 }}>
                  {uncategorizedFiles.length} file{uncategorizedFiles.length > 1 ? 's' : ''} from before Chart Order migration
                </p>
                <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
                  These files were uploaded with legacy categories. Re-upload them to the appropriate Chart Order tab to organize.
                </p>
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {uncategorizedFiles.map(f => (
                    <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#d1d5db', fontSize: '12px' }}>{f.fileName} ({f.category})</span>
                      <a href={f.downloadUrl} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#58A6FF', fontSize: '12px', textDecoration: 'none' }}>Download</a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Responses View */}
      {activeSection === 'forms' && (
        <div style={{ background: '#28527A', borderRadius: '8px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ color: '#F7E9A0', margin: 0, fontSize: '16px' }}>Completed Form Responses</h4>
            <input type="text" placeholder="Search forms..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ background: '#16325B', border: '1px solid #6CA6C1', borderRadius: '4px', padding: '6px 10px', color: '#f9fafb', width: '160px', fontSize: '13px' }} />
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #6CA6C1' }}>
                <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Form Name</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Category</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Score</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Submitted</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', color: '#f9fafb', fontSize: '13px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#d1d5db' }}>
                    {documents.length === 0 ? 'No completed forms yet' : 'No documents match your search'}
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((doc) => {
                  const score = calculateScore(doc.responses, doc.formTemplateId);
                  const severity = score !== null ? getSeverityLabel(score, doc.formTemplateId) : null;
                  return (
                    <tr key={doc.id} style={{ borderBottom: '1px solid rgba(108,166,193,0.2)' }}>
                      <td style={{ padding: '10px 8px', color: '#f9fafb', fontSize: '13px' }}>{doc.formName}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ background: '#6b7280', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{doc.category}</span>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        {score !== null && severity ? (
                          <span style={{ background: severity.color, color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                            {score} — {severity.label}
                          </span>
                        ) : <span style={{ color: '#d1d5db', fontSize: '12px' }}>\u2014</span>}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#d1d5db', fontSize: '13px' }}>{doc.submittedDate}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <button onClick={() => setSelectedDoc(doc)}
                          style={{ background: '#58A6FF', border: 'none', borderRadius: '4px', padding: '4px 12px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                          View Responses
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Client Sidebar Component
interface ClientSidebarProps {
  client: Patient;
  onEditClick: () => void;
  onScheduleAppointment?: () => void;
}

const ClientSidebar: React.FC<ClientSidebarProps> = ({ client, onEditClick, onScheduleAppointment }) => {
  const { user } = useAuth();
  const toast = useToast();
  // Collapsible state management — all collapsed by default
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    clientInfo: true,
    appointments: true,
    insurance: true,
    billing: true,
    communication: true
  });

  // Upcoming appointments state
  const [upcomingAppts, setUpcomingAppts] = useState<Appointment[]>([]);
  const [completedAppts, setCompletedAppts] = useState<Appointment[]>([]);

  useEffect(() => {
    if (!client.id) return;
    const providerId = user?.uid;
    if (!providerId) return;
    const loadAppts = async () => {
      try {
        // Query by providerId only, then filter client-side by patientId
        // This handles ID mismatches between patient doc ID and auth UID
        const allProviderAppts = await firebaseAppointmentService.getAppointments({ providerId });
        const clientAppts = allProviderAppts.filter(a =>
          a.patientId === client.id || a.patientName === `${client.firstName} ${client.lastName}`
        );

        const now = new Date();
        const upcoming = clientAppts
          .filter(a => {
            if (a.status !== 'scheduled' && a.status !== 'confirmed') return false;
            const apptDate = a.appointmentDate instanceof Date ? a.appointmentDate : new Date(a.appointmentDate);
            // Combine appointmentDate + startTime for accurate comparison
            if (a.startTime) {
              const [time, meridiem] = a.startTime.split(' ');
              const [hStr, mStr] = time.split(':');
              let hours = parseInt(hStr, 10);
              const mins = parseInt(mStr, 10) || 0;
              if (meridiem?.toUpperCase() === 'PM' && hours !== 12) hours += 12;
              if (meridiem?.toUpperCase() === 'AM' && hours === 12) hours = 0;
              const fullDate = new Date(apptDate);
              fullDate.setHours(hours, mins, 0, 0);
              return fullDate >= now;
            }
            return apptDate >= now;
          })
          .sort((a, b) => {
            const da = a.appointmentDate instanceof Date ? a.appointmentDate : new Date(a.appointmentDate);
            const db = b.appointmentDate instanceof Date ? b.appointmentDate : new Date(b.appointmentDate);
            return da.getTime() - db.getTime();
          })
          .slice(0, 5);

        setUpcomingAppts(upcoming);
        setCompletedAppts(clientAppts.filter(a => a.status === 'completed'));
      } catch (err) {
        console.error('Error loading appointments:', err);
      }
    };
    loadAppts();
  }, [client.id, user?.uid]);

  const toggleSection = (section: string) => {
    const newCollapsed = { ...collapsed, [section]: !collapsed[section] };
    setCollapsed(newCollapsed);
    localStorage.setItem('clientSidebarCollapsed', JSON.stringify(newCollapsed));
  };

  // Calculate age from date of birth
  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div style={{ width: '300px', flexShrink: 0 }}>
      {/* 1. Client Info (merged with Contacts) */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: collapsed.clientInfo ? '0' : '12px',
            cursor: 'pointer'
          }}
          onClick={() => toggleSection('clientInfo')}
        >
          <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>
            Client Info {collapsed.clientInfo ? '▶' : '▼'}
          </h3>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#58A6FF',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
          >
            Edit
          </button>
        </div>

        {!collapsed.clientInfo && (
          <div>
            {/* Demographics */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>Date of Birth</span>
                <span style={{ color: '#f9fafb', fontSize: '12px' }}>
                  {client.dateOfBirth ? (client.dateOfBirth.split('-').length === 3 ? `${client.dateOfBirth.split('-')[1]}/${client.dateOfBirth.split('-')[2]}/${client.dateOfBirth.split('-')[0]}` : new Date(client.dateOfBirth).toLocaleDateString('en-US')) : 'N/A'}
                  {client.dateOfBirth && ` (${calculateAge(client.dateOfBirth)} yrs)`}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>Phone</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#f9fafb', fontSize: '12px' }}>{client.phone || 'N/A'}</div>
                  <div style={{ color: '#d1d5db', fontSize: '10px' }}>Mobile ☎ ✉</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>Email</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#58A6FF', fontSize: '12px' }}>{client.email || 'N/A'}</div>
                  <div style={{ color: '#d1d5db', fontSize: '10px' }}>Primary ✉</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>Address</span>
                <div style={{ textAlign: 'right', color: '#f9fafb', fontSize: '12px' }}>
                  {client.address || client.city ? (
                    <>
                      {client.address && <div>{client.address}</div>}
                      {client.city && <div>{client.city}{client.state ? `, ${client.state}` : ''} {client.zipCode || ''}</div>}
                    </>
                  ) : (
                    <div>N/A</div>
                  )}
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            {client.emergencyContact && (
              <div style={{
                borderTop: '1px solid #6CA6C1',
                paddingTop: '12px'
              }}>
                <div style={{ color: '#F7E9A0', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  Emergency Contact
                </div>
                <div style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                  {client.emergencyContact.name}
                  {client.emergencyContact.relationship && ` (${client.emergencyContact.relationship})`}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: '#d1d5db', fontSize: '12px' }}>Phone</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#f9fafb', fontSize: '12px' }}>{client.emergencyContact.phone || 'N/A'}</div>
                  </div>
                </div>
                {client.emergencyContact.email && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#d1d5db', fontSize: '12px' }}>Email</span>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#58A6FF', fontSize: '12px' }}>{client.emergencyContact.email}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. Upcoming Appointments */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: collapsed.appointments ? '0' : '12px',
            cursor: 'pointer'
          }}
          onClick={() => toggleSection('appointments')}
        >
          <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>
            Upcoming Appointments {collapsed.appointments ? '▶' : '▼'}
          </h3>
        </div>

        {!collapsed.appointments && (
          <div>
            {upcomingAppts.length === 0 ? (
              <div style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '8px' }}>
                None scheduled
              </div>
            ) : (
              upcomingAppts.map(appt => (
                <div key={appt.id} style={{
                  borderBottom: '1px solid rgba(108,166,193,0.3)',
                  padding: '8px 0',
                  fontSize: '13px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ color: '#f9fafb', fontWeight: 600 }}>
                      {appt.appointmentDate instanceof Date
                        ? appt.appointmentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                        : new Date(appt.appointmentDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    <div style={{ color: '#d1d5db' }}>
                      {formatTime12(appt.startTime)} – {formatTime12(appt.endTime)} · {appt.sessionType.replace('-', ' ')}
                    </div>
                    <div style={{ color: '#58A6FF', fontSize: '12px' }}>
                      {appt.modality === 'video' ? '📹 Video' : '🏢 In-person'} · {appt.status}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const provName = user?.displayName || 'Your Provider';
                      const apptDate = appt.appointmentDate instanceof Date ? appt.appointmentDate : new Date(appt.appointmentDate);
                      try {
                        const result = await appointmentReminderService.sendAppointmentReminder({
                          appointmentId: appt.id,
                          patientName: `${client.firstName} ${client.lastName}`,
                          patientEmail: client.email || '',
                          patientPhone: client.phone || '',
                          providerName: provName,
                          appointmentDate: apptDate,
                          startTime: appt.startTime,
                          modality: appt.modality,
                          sessionType: appt.sessionType,
                        });
                        const channels = [];
                        if (result.emailSent) channels.push('email');
                        if (result.smsSent) channels.push('SMS');
                        if (channels.length > 0) {
                          toast.success('Reminder Sent', `Sent via ${channels.join(' & ')}`);
                        } else {
                          toast.info('Reminder', 'SMTP/Twilio not configured yet. Reminder logged.');
                        }
                      } catch (err) {
                        console.error('Failed to send reminder:', err);
                        toast.error('Error', 'Failed to send reminder');
                      }
                    }}
                    style={{
                      background: '#6CA6C1',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 10px',
                      color: '#0f172a',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                    title={`Send reminder to ${client.email || 'email'} / ${client.phone || 'phone'}`}
                  >
                    {appt.reminderSent ? 'Resend' : 'Remind'}
                  </button>
                </div>
              ))
            )}
            <button
              onClick={onScheduleAppointment}
              style={{
                background: 'none',
                border: 'none',
                color: '#58A6FF',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline',
                padding: 0,
                marginTop: '8px'
              }}
            >
              Schedule appointment
            </button>
          </div>
        )}
      </div>

      {/* 3. Insurance */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: collapsed.insurance ? '0' : '12px',
            cursor: 'pointer'
          }}
          onClick={() => toggleSection('insurance')}
        >
          <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>
            Insurance {collapsed.insurance ? '▶' : '▼'}
          </h3>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#58A6FF',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
          >
            Edit
          </button>
        </div>

        {!collapsed.insurance && (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Primary</div>
              <div style={{ color: '#f9fafb', fontSize: '14px', marginBottom: '2px' }}>
                {client.insurance?.provider || 'No insurance on file'}
              </div>
              {client.insurance?.subscriberName && (
                <div style={{ color: '#f9fafb', fontSize: '14px', marginBottom: '2px' }}>
                  Subscriber: {client.insurance.subscriberName}
                </div>
              )}
              {client.insurance?.policyNumber && (
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '2px' }}>
                  Policy #: {client.insurance.policyNumber}
                </div>
              )}
              {client.insurance?.groupNumber && (
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '8px' }}>
                  Group ID: {client.insurance.groupNumber}
                </div>
              )}
              {client.insurance && (
                <div style={{ color: '#58A6FF', fontSize: '14px', cursor: 'pointer' }}>
                  Request insurance status check
                </div>
              )}
            </div>

            {client.insurance && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ color: '#d1d5db', fontSize: '14px' }}>Insurance balance</span>
                  <span style={{ color: '#EF4444', fontSize: '14px' }}>$0 ▼</span>
                </div>

                <button style={{
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '14px'
                }}>
                  Add insurance payment
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* 4. Client Billing (merged with Invoices) */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: collapsed.billing ? '0' : '16px',
            cursor: 'pointer'
          }}
          onClick={() => toggleSection('billing')}
        >
          <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>
            Client Billing {collapsed.billing ? '▶' : '▼'}
          </h3>
        </div>

        {!collapsed.billing && (() => {
          const getCptFee = (st: string) => SESSION_CPT[st]?.fee || 175;
          const totalBilled = completedAppts.reduce((sum, a) => sum + getCptFee(a.sessionType), 0);
          const hasIns = !!client.insurance?.provider;
          const insurancePortion = hasIns ? totalBilled * 0.8 : 0;
          const clientOwes = totalBilled - insurancePortion;
          const uninvoicedCount = completedAppts.length;

          return (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#d1d5db', fontSize: '14px' }}>Total billed</span>
                  <span style={{ color: '#f9fafb', fontSize: '14px' }}>${totalBilled.toFixed(2)}</span>
                </div>
                {hasIns && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#d1d5db', fontSize: '14px' }}>Insurance portion</span>
                    <span style={{ color: '#10B981', fontSize: '14px' }}>-${insurancePortion.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#d1d5db', fontSize: '14px' }}>Client balance</span>
                  <span style={{ color: clientOwes > 0 ? '#F7E9A0' : '#f9fafb', fontSize: '14px', fontWeight: 600 }}>
                    ${clientOwes.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#58A6FF', fontSize: '14px', cursor: 'pointer' }}>
                    Uninvoiced ({uninvoicedCount})
                  </span>
                  <span style={{ color: '#f9fafb', fontSize: '14px' }}>${clientOwes.toFixed(2)}</span>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #6CA6C1', paddingTop: '12px', marginBottom: '12px' }}>
                <div style={{ color: '#F7E9A0', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  Sessions ({completedAppts.length})
                </div>
                {completedAppts.length === 0 ? (
                  <div style={{ color: '#d1d5db', fontSize: '14px' }}>No completed sessions.</div>
                ) : (
                  <div style={{ color: '#d1d5db', fontSize: '13px' }}>
                    {completedAppts.length} session{completedAppts.length > 1 ? 's' : ''} completed
                  </div>
                )}
              </div>

              <button style={{
                background: '#10B981', border: 'none', borderRadius: '4px',
                padding: '8px 16px', color: 'white', cursor: 'pointer', width: '100%', fontSize: '14px'
              }}>
                Add Payment
              </button>
            </div>
          );
        })()}
      </div>

      {/* 5. Communication Settings */}
      <div style={{
        background: '#28527A',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: collapsed.communication ? '0' : '12px',
            cursor: 'pointer'
          }}
          onClick={() => toggleSection('communication')}
        >
          <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '16px' }}>
            Communication Settings {collapsed.communication ? '▶' : '▼'}
          </h3>
        </div>

        {!collapsed.communication && (
          <div style={{ color: '#d1d5db', fontSize: '14px' }}>
            No communication preferences set.
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientProfile;